<script lang="ts">
  import { onMount, onDestroy, mount } from 'svelte'
  import { Editor, Extension } from '@tiptap/core'
  import StarterKit from '@tiptap/starter-kit'
  import Placeholder from '@tiptap/extension-placeholder'
  import Image from '@tiptap/extension-image'
  import Link from '@tiptap/extension-link'
  import Youtube from '@tiptap/extension-youtube'
  import HardBreak from '@tiptap/extension-hard-break'
  import { Table } from '@tiptap/extension-table'
  import { TableRow } from '@tiptap/extension-table-row'
  import { TableHeader } from '@tiptap/extension-table-header'
  import { TableCell } from '@tiptap/extension-table-cell'
  import { Markdown } from 'tiptap-markdown'
  import { Plugin, PluginKey } from '@tiptap/pm/state'
  import { Decoration, DecorationSet } from '@tiptap/pm/view'
  import { InputRule, PasteRule } from '@tiptap/core'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { searchStore, type SearchMatch } from '$lib/stores/searchStore'
  import { SearchHighlight } from '$lib/tiptap/SearchHighlight'
  import { InlineViewHighlight } from '$lib/tiptap/InlineViewHighlight'
  import { MarkdownRenderer } from '$lib/tiptap/MarkdownRenderer'
  import { inlineViewStore } from '$lib/stores/inlineView'
  import { get } from 'svelte/store'
  import { toggleHeadingWithSelection } from '$lib/tiptap/shortcutUtils'
  import { getVaultRelativePath, generateImageMarkdown } from '$lib/utils/imageUtils'
  import ImageSizeMenu from '$components/ImageSizeMenu.svelte'
  import { imageResizeEvents, imageDeleteEvents } from '$lib/stores/imageResizeStore'

  interface Props {
    content: string
    onContentChange: (content: string) => void
    onSelectionChange?: () => void
    onBackspaceAtStart?: () => void
    onParaphrase?: () => void
    onExpand?: () => void
    onShrink?: () => void
    onFileDrop?: (fileId: string) => void
    class?: string
    placeholder?: string
    readonly?: boolean
    disabled?: boolean
    isPromptOpen?: boolean
    promptHeight?: number
  }

  let {
    content = '',
    onContentChange,
    onSelectionChange,
    onBackspaceAtStart,
    onParaphrase,
    onExpand,
    onShrink,
    onFileDrop,
    class: className = '',
    placeholder = '내용을 입력하세요...',
    readonly = false,
    disabled = false,
    isPromptOpen = false,
    promptHeight = 0
  }: Props = $props()
  
  let editorElement: HTMLDivElement
  let editor: Editor | null = null
  let markdownRenderer: MarkdownRenderer | null = null
  let isUpdating = false
  let lastContent = ''
  let characterCount = $state(0)
  const CONTENT_UPDATE_DELAY = 32 // ms throttle window for markdown serialization
  let pendingContentUpdate: ReturnType<typeof setTimeout> | null = null

  // YouTube Relay configuration for Electron environment (fixes error 153)
  // Use pages.dev domain directly as custom domain may have SPA routing
const YOUTUBE_RELAY_URL = 'https://korean-grammar-svelte.pages.dev/youtube-relay.html'

  function isElectronEnvironment(): boolean {
    return typeof window !== 'undefined' &&
           ((window as any).electronAPI !== undefined || (window as any).api !== undefined)
  }

  function getYouTubeEmbedUrl(videoId: string): string {
    if (isElectronEnvironment()) {
      // Use relay page in Electron to avoid error 153
      return `${YOUTUBE_RELAY_URL}?v=${encodeURIComponent(videoId)}`
    }
    // Direct embed for web environment
    return `https://www.youtube-nocookie.com/embed/${videoId}`
  }
  let linkPreviewElement: HTMLDivElement | null = null
  let linkPreviewUrlElement: HTMLSpanElement | null = null
  let linkPreviewOpenButton: HTMLButtonElement | null = null
  let linkPreviewEditButton: HTMLButtonElement | null = null
  let linkPreviewRemoveButton: HTMLButtonElement | null = null
  let linkPreviewHideTimer: number | null = null
  let activeLinkElement: HTMLAnchorElement | null = null
  let linkPreviewCleanup: (() => void) | null = null
  let lastPreviewAnchor: HTMLAnchorElement | null = null
  let linkPreviewRaf = 0
  let isLinkPreviewScrolling = false
  let linkPreviewScrollTimeout: number | null = null
  let lastInlineHoverId: string | null = null

  function scheduleLinkPreviewPosition() {
    if (!lastPreviewAnchor || !linkPreviewElement || !linkPreviewElement.classList.contains('is-visible')) {
      return
    }

    if (isLinkPreviewScrolling) {
      return
    }

    if (linkPreviewRaf) {
      return
    }

    linkPreviewRaf = requestAnimationFrame(() => {
      linkPreviewRaf = 0

      if (!lastPreviewAnchor || !linkPreviewElement || !linkPreviewElement.classList.contains('is-visible')) {
        return
      }

      positionLinkPreview(lastPreviewAnchor, linkPreviewElement)
    })
  }

  function cancelScheduledContentUpdate() {
    if (pendingContentUpdate !== null) {
      clearTimeout(pendingContentUpdate)
      pendingContentUpdate = null
    }
  }

  function runContentUpdate() {
    if (!editor || editor.isDestroyed || isUpdating) {
      return
    }

    const storage = editor.storage.markdown
    if (!storage || typeof storage.getMarkdown !== 'function') {
      return
    }

    let markdown = storage.getMarkdown() ?? ''
    const doc = editor.state.doc

    if (markdown.includes('[youtube]')) {
      const youtubeNodes: { url: string }[] = []
      doc.descendants((node) => {
        if (node.type.name === 'youtube' && node.attrs.src) {
          youtubeNodes.push({ url: node.attrs.src })
        }
      })

      if (youtubeNodes.length > 0) {
        let replacedCount = 0
        markdown = markdown.replace(/\[youtube\]/g, () => {
          const node = youtubeNodes[replacedCount]
          replacedCount++
          return node?.url ?? '[youtube]'
        })
      }
    }

    if (markdown.includes('\\')) {
      // Fix escaped special characters
      markdown = markdown.replace(/\\(\*|_|`|~|#|>|-)/g, '$1')

      // Fix escaped image syntax: !\[\](...) -> ![](...)
      markdown = markdown.replace(/!\\\[(.*?)\\\]\(([^)]+)\)/g, '![$1]($2)')

      // Fix escaped link syntax: \[text\](url) -> [text](url)
      // But not for images (handled above) or footnotes (handled below)
      markdown = markdown.replace(/(?<!!)\\\[((?!\^).*?)\\\]\(([^)]+)\)/g, '[$1]($2)')
    }

    if (markdown.includes('\\[')) {
      // Fix escaped footnote brackets
      markdown = markdown
        .replace(/\\\[(\^[^\]]+)\\\]:/g, '[$1]:')
        .replace(/\\\[(\^[^\]]+)\\\]/g, '[$1]')
    }

    if (markdown.includes('[^')) {
      const lines = markdown.split('\n')
      const cleanedLines: string[] = []
      let skipNextEmptyLine = false

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const nextLine = lines[i + 1]

        if (line === '---' && nextLine && nextLine.match(/^\[?\^[\w]+\]?:/)) {
          continue
        }

        if (line === '' && skipNextEmptyLine) {
          skipNextEmptyLine = false
          continue
        }

        if (line.match(/^\[?\^[\w]+\]?:/)) {
          cleanedLines.push(line)
          if (lines[i + 1] === '') {
            skipNextEmptyLine = true
          }
        } else {
          cleanedLines.push(line)
        }
      }

      markdown = cleanedLines.join('\n')
    }

    const finalMarkdown = preserveYamlFrontmatter(originalContentWithYaml || content, markdown)

    if (finalMarkdown !== lastContent) {
      lastContent = finalMarkdown
      isUpdating = true
      onContentChange(finalMarkdown)
      queueMicrotask(() => {
        isUpdating = false
      })
    }
  }

  function flushPendingContentUpdate() {
    if (pendingContentUpdate !== null) {
      cancelScheduledContentUpdate()
      runContentUpdate()
    }
  }

  function handleFlushRequest() {
    flushPendingContentUpdate()
  }

  function scheduleContentUpdate() {
    if (pendingContentUpdate !== null) {
      return
    }

    if (typeof window === 'undefined') {
      runContentUpdate()
      return
    }

    pendingContentUpdate = window.setTimeout(() => {
      pendingContentUpdate = null
      runContentUpdate()
    }, CONTENT_UPDATE_DELAY)
  }

  // Pre-load all images from templates using Vite glob
  const imageModules = import.meta.glob('/src/lib/templates/**/*.{png,jpg,jpeg,gif,svg,webp,bmp}', { eager: true, import: 'default' })

  // Store original content with YAML
  let originalContentWithYaml = $state('')
  
  // Helper function to remove YAML frontmatter
  function removeYamlFrontmatter(text: string): string {
    const yamlRegex = /^---\n[\s\S]*?\n---\n/
    return text.replace(yamlRegex, '')
  }

  // Helper to encode Korean/special characters in image paths for markdown-it parsing
  function encodeImagePaths(markdown: string): { encoded: string; map: Map<string, string> } {
    const map = new Map<string, string>()
    let counter = 0

    const encoded = markdown.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
      // Check if src contains Korean characters or spaces
      if (/[가-힣\s]/.test(src)) {
        const placeholder = `__IMAGE_PLACEHOLDER_${counter++}__`
        map.set(placeholder, src)
        return `![${alt}](${placeholder})`
      }
      return match
    })

    return { encoded, map }
  }

  // Helper to decode image paths back after parsing
  function decodeImagePaths(editor: any, map: Map<string, string>) {
    if (map.size === 0) return

    const tr = editor.state.tr
    let modified = false

    editor.state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === 'image' && node.attrs.src) {
        const placeholder = node.attrs.src
        const originalSrc = map.get(placeholder)

        if (originalSrc) {
          // Replace placeholder with original Korean path
          tr.setNodeMarkup(pos, null, { ...node.attrs, src: originalSrc })
          modified = true
        }
      }
    })

    if (modified) {
      editor.view.dispatch(tr)
    }
  }

  // Custom copy handler to copy as markdown
  function handleCopy(event: ClipboardEvent) {
    if (!editor) return

    const { from, to } = editor.state.selection

    // Get the selected content
    const slice = editor.state.doc.slice(from, to)

    // Convert to markdown
    const markdownSerializer = editor.storage.markdown?.serializer
    if (markdownSerializer && event.clipboardData) {
      try {
        const markdown = markdownSerializer.serialize(slice.content)

        // Clear all existing clipboard data
        event.clipboardData.clearData()

        // Set ONLY plain text as markdown
        event.clipboardData.setData('text/plain', markdown)

        // Explicitly prevent HTML from being copied
        event.clipboardData.setData('text/html', '')

        // Prevent default copy behavior (which includes HTML)
        event.preventDefault()
        event.stopPropagation()
      } catch (error) {
        console.error('[TipTap] Failed to copy as markdown:', error)
        // Let default behavior happen on error
      }
    }
  }
  
  // Track double Cmd+Enter for paragraph continuation
  let lastEnterTime = 0
  let enterCount = 0
  let enterTimer: number | null = null
  
  

  // Link preview helpers
  function ensureLinkPreviewElement(): HTMLDivElement {
    if (!linkPreviewElement) {
      const container = document.createElement('div')
      container.className = 'tiptap-link-preview'

      const urlElement = document.createElement('span')
      urlElement.className = 'tiptap-link-preview-url'

      const actionsElement = document.createElement('div')
      actionsElement.className = 'tiptap-link-preview-actions'

      const buttonsElement = document.createElement('div')
      buttonsElement.className = 'tiptap-link-preview-buttons'

      const openButton = document.createElement('button')
      openButton.type = 'button'
      openButton.className = 'tiptap-link-preview-button primary'
      openButton.textContent = '링크 열기'
      openButton.setAttribute('aria-label', '링크 열기')

      const editButton = document.createElement('button')
      editButton.type = 'button'
      editButton.className = 'tiptap-link-preview-button secondary'
      editButton.textContent = '수정'
      editButton.setAttribute('aria-label', '링크 수정')

      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'tiptap-link-preview-button danger'
      removeButton.textContent = '삭제'
      removeButton.setAttribute('aria-label', '링크 삭제')

      const hintElement = document.createElement('span')
      hintElement.className = 'tiptap-link-preview-hint'
      hintElement.textContent = navigator.platform.includes('Mac') ? '⌘ 클릭으로 열기' : 'Ctrl 클릭으로 열기'

      buttonsElement.appendChild(openButton)
      buttonsElement.appendChild(editButton)
      buttonsElement.appendChild(removeButton)

      actionsElement.appendChild(buttonsElement)
      actionsElement.appendChild(hintElement)

      container.appendChild(urlElement)
      container.appendChild(actionsElement)

      container.addEventListener('mouseenter', () => {
        if (linkPreviewHideTimer) {
          clearTimeout(linkPreviewHideTimer)
          linkPreviewHideTimer = null
        }
      })

      container.addEventListener('mouseleave', () => {
        hideLinkPreview(true)
      })

      document.body.appendChild(container)

      linkPreviewElement = container
      linkPreviewUrlElement = urlElement
      linkPreviewOpenButton = openButton
      linkPreviewEditButton = editButton
      linkPreviewRemoveButton = removeButton

      linkPreviewOpenButton.disabled = true
      linkPreviewEditButton.disabled = true
      linkPreviewRemoveButton.disabled = true
    }

    return linkPreviewElement!
  }

  function positionLinkPreview(anchor: HTMLElement, container: HTMLDivElement) {
    const anchorRect = anchor.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const scrollX = window.scrollX || document.documentElement.scrollLeft
    const scrollY = window.scrollY || document.documentElement.scrollTop
    const verticalOffset = 12
    const horizontalMargin = 16

    let left = anchorRect.left + scrollX
    const maxLeft = scrollX + window.innerWidth - containerRect.width - horizontalMargin
    if (left > maxLeft) {
      left = maxLeft
    }
    if (left < scrollX + horizontalMargin) {
      left = scrollX + horizontalMargin
    }

    const top = anchorRect.bottom + scrollY + verticalOffset

    container.style.left = `${Math.round(left)}px`
    container.style.top = `${Math.round(top)}px`
  }

  function focusLinkFromAnchor(anchor: HTMLAnchorElement): boolean {
    if (!editor || !editor.view) return false

    const rect = anchor.getBoundingClientRect()
    const coords = {
      left: rect.left + rect.width / 2,
      top: rect.top + rect.height / 2
    }

    const posInfo = editor.view.posAtCoords(coords)
    if (!posInfo) {
      console.warn('[TipTap] Unable to resolve position for link preview anchor')
      return false
    }

    const selectionApplied = editor.chain()
      .focus()
      .setTextSelection(posInfo.pos)
      .extendMarkRange('link')
      .run()

    return selectionApplied
  }

  function openLinkExternally(href: string) {
    if (!href) return
    try {
      window.open(href, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('[TipTap] Failed to open link:', error)
    }
  }

  function showLinkPreview(anchor: HTMLAnchorElement) {
    const href = anchor.getAttribute('href')?.trim() || ''
    if (!href) {
      hideLinkPreview(false)
      return
    }

    if (
      activeLinkElement === anchor &&
      linkPreviewElement?.classList.contains('is-visible')
    ) {
      lastPreviewAnchor = anchor
      return
    }

    const container = ensureLinkPreviewElement()
    if (!linkPreviewUrlElement || !linkPreviewOpenButton) return

    if (linkPreviewHideTimer) {
      clearTimeout(linkPreviewHideTimer)
      linkPreviewHideTimer = null
    }

    linkPreviewUrlElement.textContent = href
    linkPreviewUrlElement.title = href

    linkPreviewOpenButton.disabled = false
    linkPreviewOpenButton.onclick = async (event) => {
      event.preventDefault()
      event.stopPropagation()

      // Check if demo mode
      const urlParams = new URLSearchParams(window.location.search)
      const isDemoMode = urlParams.get('demo') === 'true'

      // In demo mode, handle internal document links
      if (isDemoMode && (href.startsWith('./') || href.startsWith('../'))) {
        // Map file names to document titles (without .md extension)
        const fileToTitleMap: Record<string, string> = {
          '시작하기.md': '시작하기',
          '🤖 AI를 글쓰기에 활용하기.md': '🤖 AI를 글쓰기에 활용하기',
          '⚙️ 기본 개념 설명.md': '⚙️ 기본 개념 설명'
        }

        // Extract filename from path
        const filename = href.split('/').pop() || ''
        const documentTitle = fileToTitleMap[filename]

        if (documentTitle) {
          // Find and open the document
          const { fileStore } = await import('$lib/stores/fileStore')
          const { get } = await import('svelte/store')
          const state = get(fileStore)

          const doc = state.documents.find((d: any) => d.title === documentTitle)
          if (doc) {
            await fileStore.openFile(doc.id)
            hideLinkPreview(false)
          }
        }
      } else if (!isDemoMode || href.startsWith('http://') || href.startsWith('https://')) {
        // Only open external links if not in demo mode, or if explicitly external
        openLinkExternally(href)
        hideLinkPreview(false)
      }
    }

    if (linkPreviewEditButton) {
      linkPreviewEditButton.disabled = false
      linkPreviewEditButton.onclick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        const focused = focusLinkFromAnchor(anchor)
        hideLinkPreview(false)
        if (!focused) {
          // Still attempt to show link editor even if focus fails
          console.warn('[TipTap] Failed to focus link before editing from preview')
        }
        window.dispatchEvent(new CustomEvent('readingMode:toggle-link'))
      }
    }

    if (linkPreviewRemoveButton) {
      linkPreviewRemoveButton.disabled = false
      linkPreviewRemoveButton.onclick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        const focused = focusLinkFromAnchor(anchor)
        if (!focused) {
          console.warn('[TipTap] Failed to focus link before removal from preview')
        }
        editor?.chain().focus().unsetLink().run()
        hideLinkPreview(false)
      }
    }

    activeLinkElement?.classList.remove('is-hovered-link')
    activeLinkElement = anchor
    activeLinkElement.classList.add('is-hovered-link')

    container.classList.add('is-visible')
    container.style.visibility = 'hidden'
    container.style.left = '-9999px'
    container.style.top = '-9999px'
    positionLinkPreview(anchor, container)
    lastPreviewAnchor = anchor
    container.style.visibility = 'visible'
  }

  function hideLinkPreview(withDelay: boolean) {
    if (!linkPreviewElement) return

    const performHide = () => {
      if (!linkPreviewElement) return
      linkPreviewElement.classList.remove('is-visible')
      activeLinkElement?.classList.remove('is-hovered-link')
      activeLinkElement = null
      if (linkPreviewOpenButton) {
        linkPreviewOpenButton.disabled = true
        linkPreviewOpenButton.onclick = null
      }
      if (linkPreviewEditButton) {
        linkPreviewEditButton.disabled = true
        linkPreviewEditButton.onclick = null
      }
      if (linkPreviewRemoveButton) {
        linkPreviewRemoveButton.disabled = true
        linkPreviewRemoveButton.onclick = null
      }
      lastPreviewAnchor = null
      linkPreviewHideTimer = null
    }

    if (linkPreviewHideTimer) {
      clearTimeout(linkPreviewHideTimer)
      linkPreviewHideTimer = null
    }

    if (withDelay) {
      linkPreviewHideTimer = window.setTimeout(performHide, 120)
    } else {
      performHide()
    }
  }

  function setupLinkPreviewHandlers() {
    if (!editor || !editor.view || linkPreviewCleanup) return

    const dom = editor.view.dom as HTMLElement
    if (!dom) return

    const handleClick = async (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.getAttribute('href') === null) {
        return
      }

      const href = anchor.getAttribute('href') || ''

      // Check if demo mode
      const urlParams = new URLSearchParams(window.location.search)
      const isDemoMode = urlParams.get('demo') === 'true'

      if (event.metaKey || event.ctrlKey) {
        event.preventDefault()
        event.stopPropagation()
        openLinkExternally(href)
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (isDemoMode) {
        if (href.startsWith('./') || href.startsWith('../')) {
          const fileToTitleMap: Record<string, string> = {
            '시작하기.md': '시작하기',
            '🤖 AI를 글쓰기에 활용하기.md': '🤖 AI를 글쓰기에 활용하기',
            '⚙️ 기본 개념 설명.md': '⚙️ 기본 개념 설명'
          }

          const filename = href.split('/').pop() || ''
          const documentTitle = fileToTitleMap[filename]

          if (documentTitle) {
            const { fileStore } = await import('$lib/stores/fileStore')
            const { get } = await import('svelte/store')
            const state = get(fileStore)

            const doc = state.documents.find((d: any) => d.title === documentTitle)
            if (doc) {
              await fileStore.openFile(doc.id)
              hideLinkPreview(false)
              return
            }
          }
        }
      }

      showLinkPreview(anchor)
    }

    const handleScroll = () => {
      if (!activeLinkElement || !linkPreviewElement || !linkPreviewElement.classList.contains('is-visible')) {
        hideLinkPreview(false)
        return
      }

      isLinkPreviewScrolling = true

      if (linkPreviewRaf) {
        cancelAnimationFrame(linkPreviewRaf)
        linkPreviewRaf = 0
      }

      if (linkPreviewScrollTimeout) {
        clearTimeout(linkPreviewScrollTimeout)
      }

      linkPreviewScrollTimeout = window.setTimeout(() => {
        isLinkPreviewScrolling = false
        scheduleLinkPreviewPosition()
      }, 80)
    }
    const handleResize = () => hideLinkPreview(false)

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!linkPreviewElement || !linkPreviewElement.classList.contains('is-visible')) {
        return
      }

      const target = event.target as HTMLElement
      if (target && (linkPreviewElement.contains(target) || activeLinkElement?.contains(target))) {
        return
      }

      hideLinkPreview(false)
    }

    dom.addEventListener('click', handleClick)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    document.addEventListener('pointerdown', handlePointerDownOutside, true)

    linkPreviewCleanup = () => {
      dom.removeEventListener('click', handleClick)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
      if (linkPreviewRaf) {
        cancelAnimationFrame(linkPreviewRaf)
        linkPreviewRaf = 0
      }
      if (linkPreviewScrollTimeout) {
        clearTimeout(linkPreviewScrollTimeout)
        linkPreviewScrollTimeout = null
      }
      isLinkPreviewScrolling = false
    }
  }

  // Handler for local image paths
  async function handleLocalImagePath(localPath: string, view: any) {
    // Clean up the path (remove quotes, trim whitespace)
    let cleanPath = localPath.trim()
    if ((cleanPath.startsWith('"') && cleanPath.endsWith('"')) || 
        (cleanPath.startsWith("'") && cleanPath.endsWith("'"))) {
      cleanPath = cleanPath.slice(1, -1)
    }
    
    // Keep original path format for IPC (Windows needs backslashes)
    const pathForIPC = cleanPath
    
    try {
      // Check if we're in Electron environment
      if ((window as any).electronAPI?.copyLocalImage) {
        const result = await (window as any).electronAPI.copyLocalImage(pathForIPC)
        
        if (result?.success && result.path) {
          // Insert image with relative path
          const { from } = view.state.selection
          const node = view.state.schema.nodes.image.create({
            src: result.path
          })
          const transaction = view.state.tr.replaceSelectionWith(node)
          view.dispatch(transaction)
          
          // Dispatch event to refresh file explorer
          window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
        } else {
          console.error('Failed to copy local image:', result?.error)
          // Fallback: insert normalized path so vault images still render
          const normalizedPath = normalizeImagePath(cleanPath)
          const { from } = view.state.selection
          const node = view.state.schema.nodes.image.create({
            src: normalizedPath
          })
          const transaction = view.state.tr.replaceSelectionWith(node)
          view.dispatch(transaction)
        }
      } else {
        // Web environment: just insert the path as-is
        const normalizedPath = normalizeImagePath(cleanPath)
        const { from } = view.state.selection
        const node = view.state.schema.nodes.image.create({
          src: normalizedPath
        })
        const transaction = view.state.tr.replaceSelectionWith(node)
        view.dispatch(transaction)
      }
    } catch (error) {
      console.error('Error handling local image path:', error)
    }
  }
  
  // Custom extension to handle keyboard shortcuts - create a factory function
  function createKeyboardShortcuts(onBackspaceCallback?: () => void) {
    return Extension.create({
      name: 'keyboardShortcuts',
      priority: 1000, // High priority to override other extensions
      
      addKeyboardShortcuts() {
        return {
          // Intercept undo - prevent on completely empty documents
          'Mod-z': () => {
            const { editor } = this
            const currentContent = editor.state.doc.textContent.trim()
            
            // Only block undo if document is completely empty
            // This prevents cross-tab content from appearing in empty tabs
            if (currentContent.length === 0 || editor.isEmpty) {
              return true // Block undo on empty document
            }
            
            // Let normal undo proceed for documents with any content
            return false
          },
          // Also intercept redo
          'Shift-Mod-z': () => {
            const { editor } = this
            const currentContent = editor.state.doc.textContent.trim()
            
            // Only block redo on completely empty documents
            if (currentContent.length === 0 || editor.isEmpty) {
              return true // Block redo on empty document
            }
            
            return false
          },
          'Mod-y': () => {
            const { editor } = this
            const currentContent = editor.state.doc.textContent.trim()
            
            // Only block redo on completely empty documents
            if (currentContent.length === 0 || editor.isEmpty) {
              return true // Block redo on empty document
            }
            
            return false
          },
          // Handle Backspace at the start of the document
          'Backspace': () => {
            // Check if cursor is at the very beginning of the document
            const { from, to } = this.editor.state.selection
            const selection = this.editor.state.selection
            const resolvedFrom = selection.$from

            // Check if we're at the start of the first paragraph
            // Position 1 is typically the start of the first paragraph in ProseMirror
            const isAtStart = from <= 1 && to <= 1 && resolvedFrom.parentOffset === 0

            if (isAtStart) {
              // Call the callback to focus title
              onBackspaceCallback?.()
              return true // Prevent default backspace behavior
            }

            return false // Let default backspace behavior continue
          },
          // Override Cmd+Enter to dispatch our custom event
          'Mod-Enter': () => {
            const now = Date.now()
            const timeSinceLastEnter = now - lastEnterTime
            
            // Clear existing timer
            if (enterTimer) {
              clearTimeout(enterTimer)
              enterTimer = null
            }
            
            // If it's been less than 300ms since last enter, it's a double tap
            if (timeSinceLastEnter < 300) {
              enterCount++
              if (enterCount === 2) {
                // Double enter - continue paragraph
                window.dispatchEvent(new CustomEvent('shortcut:continue-paragraph'))
                enterCount = 0
                lastEnterTime = 0
                return true
              }
            } else {
              // First enter
              enterCount = 1
              lastEnterTime = now
              
              // Set timer to execute single enter action if no second enter comes
              enterTimer = window.setTimeout(() => {
                if (enterCount === 1) {
                  // Single enter - continue sentence
                  window.dispatchEvent(new CustomEvent('shortcut:continue-sentence'))
                }
                enterCount = 0
                lastEnterTime = 0
                enterTimer = null
              }, 300)
            }
            
            // Return true to prevent default behavior
            return true
          },
          // Handle Cmd+K for prompt-based generation
          'Mod-k': ({ editor }) => {
            // Highlight selection if there is one
            const { from, to } = editor.state.selection
            if (from !== to) {
              // Add highlight decoration for selected text
              editor.chain()
                .command(({ tr }) => {
                  tr.setMeta('generationSelectionHighlight', { action: 'set', from, to })
                  return true
                })
                .run()
            }

            // Dispatch the generate event
            window.dispatchEvent(new CustomEvent('shortcut:generate'))
            return true
          }
        }
      }
    })
  }
  
  // Custom HardBreak extension that only responds to Shift+Enter
  const CustomHardBreak = HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        'Shift-Enter': () => this.editor.commands.setHardBreak(),
        // Explicitly remove Mod-Enter binding
        'Mod-Enter': () => false
      }
    }
  })
  
  // Custom extension to ensure text formatting and toolbar shortcuts work
  function createFormattingShortcuts() {
    return Extension.create({
      name: 'formattingShortcuts',
      priority: 100, // Lower priority to not override other extensions

      addKeyboardShortcuts() {
        return {
          // Bold formatting
          'Mod-b': () => this.editor.chain().focus().toggleBold().run(),
          // Italic formatting
          'Mod-i': () => this.editor.chain().focus().toggleItalic().run(),
          // Strikethrough formatting
          'Shift-Mod-d': () => this.editor.chain().focus().toggleStrike().run(),
          // Inline code formatting
          'Shift-Mod-e': () => this.editor.chain().focus().toggleCode().run(),
          // Open link popover in reading toolbar
          'Mod-l': () => {
            const editorInstance = this.editor

            if (!editorInstance || editorInstance.isDestroyed) {
              return true
            }

            const { from, to } = editorInstance.state.selection

            // Ignore if no selection and cursor not inside a link
            if (from === to && !editorInstance.isActive('link')) {
              return true
            }

            window.dispatchEvent(new CustomEvent('readingMode:toggle-link'))
            return true
          },
          // Copy as HTML (rich format)
          'Shift-Mod-h': () => {
            window.dispatchEvent(new CustomEvent('readingMode:copy-html'))
            return true
          },
          // Heading shortcuts (Cmd/Ctrl + Alt + number)
          'Alt-Mod-1': () => toggleHeadingWithSelection(this.editor, 1),
          'Alt-Mod-2': () => toggleHeadingWithSelection(this.editor, 2),
          'Alt-Mod-3': () => toggleHeadingWithSelection(this.editor, 3)
        }
      }
    })
  }
  
  function normalizeImagePath(rawPath: string): string {
    if (!rawPath) return rawPath

    const trimmed = rawPath.trim()

    if (/^(https?:|data:|blob:)/i.test(trimmed)) {
      return trimmed
    }

    const vault = get(vaultStore)
    const vaultPath = vault?.vaultPath || ''

    let candidate = trimmed

    if (candidate.toLowerCase().startsWith('file://')) {
      candidate = candidate.replace(/^file:\/\//i, '')
    }

    try {
      candidate = decodeURIComponent(candidate)
    } catch (_) {
      // Ignore decoding errors and fallback to trimmed value
    }

    candidate = candidate.replace(/\\/g, '/')

    if (vaultPath) {
      const relative = getVaultRelativePath(candidate, vaultPath)
      if (relative !== candidate) {
        return relative
      }
    }

    return trimmed
  }

  // Helper function to process Obsidian-style image paths
  function processImagePaths(markdown: string): string {
    // First, unescape any escaped image markdown: !\[\](...) -> ![](...)
    markdown = markdown.replace(/!\\\[(.*?)\\\]\(([^)]+)\)/g, (match, p1, p2) => {
      return `![${p1}](${normalizeImagePath(p2)})`
    })

    // Convert Obsidian-style ![[image.png]] to standard markdown
    markdown = markdown.replace(/!\[\[([^\]]+)\]\]/g, (match, filename) => {
      return `![](${normalizeImagePath(filename)})`
    })

    // Normalize standard markdown image paths
    markdown = markdown.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
      return `![${alt}](${normalizeImagePath(src)})`
    })

    return markdown
  }
  
  // Helper function to process footnotes for better display
  function processFootnotesForDisplay(markdown: string): string {
    // Split content into main content and footnotes
    const lines = markdown.split('\n')
    const processedLines: string[] = []
    let inFootnoteSection = false
    let hasAddedSeparator = false
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const prevLine = i > 0 ? lines[i - 1] : ''
      
      // Check if this line starts with a footnote definition
      if (line.match(/^\[?\^[\w]+\]?:/)) {
        // Check if separator already exists before this footnote
        if (!inFootnoteSection && !hasAddedSeparator) {
          // Only add separator if there isn't already one
          if (prevLine !== '---') {
            processedLines.push('') // Empty line before footnotes
            processedLines.push('---') // Horizontal rule
            processedLines.push('') // Empty line after rule
            hasAddedSeparator = true
          }
          inFootnoteSection = true
        }
        // Add the footnote with extra line break after
        processedLines.push(line)
        processedLines.push('') // Empty line after each footnote
      } else {
        // Skip existing separator lines that come right before footnotes
        if (line === '---' && i + 1 < lines.length && lines[i + 1].match(/^\[?\^[\w]+\]?:/)) {
          hasAddedSeparator = true
          continue
        }
        // Regular content
        processedLines.push(line)
        // If we were in footnote section but hit regular content, we're out
        if (inFootnoteSection && line.trim() && !line.startsWith('---')) {
          inFootnoteSection = false
        }
      }
    }
    
    return processedLines.join('\n')
  }
  
  // Helper function to preserve YAML frontmatter when saving
  function preserveYamlFrontmatter(originalText: string, newContent: string): string {
    const yamlMatch = originalText.match(/^---\n[\s\S]*?\n---\n/)
    if (yamlMatch) {
      return yamlMatch[0] + newContent
    }
    return newContent
  }
  
  
  // React to image resize events from store
  $effect(() => {
    const resizeEvent = $imageResizeEvents

    if (resizeEvent && editorElement) {
      const { imagePath, timestamp } = resizeEvent

      // Find all images in the editor with this path
      const images = editorElement.querySelectorAll('.tiptap-image')
      images.forEach((img: Element) => {
        const htmlImg = img as HTMLImageElement
        const currentSrc = htmlImg.getAttribute('src') || ''

        // Check if this image matches the resized image path
        // Handle both file:// URLs and relative paths, with proper encoding/decoding
        try {
          const decodedSrc = decodeURI(currentSrc)
          const encodedPath = encodeURI(imagePath)

          // Check both encoded and decoded versions for compatibility
          const matches =
            decodedSrc.includes(imagePath) ||
            decodedSrc.endsWith(imagePath) ||
            currentSrc.includes(encodedPath) ||
            currentSrc.endsWith(encodedPath)

          if (matches) {
            // Force reload by updating src with timestamp
            const baseUrl = currentSrc.split('?')[0]
            htmlImg.src = `${baseUrl}?t=${timestamp}`
          }
        } catch (e) {
          // Fallback to simple string matching if encoding/decoding fails
          if (currentSrc.includes(imagePath) || currentSrc.endsWith(imagePath)) {
            const baseUrl = currentSrc.split('?')[0]
            htmlImg.src = `${baseUrl}?t=${timestamp}`
          }
        }
      })
    }
  })

  // Initialize TipTap editor with Markdown support
  onMount(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('flush-editor-content', handleFlushRequest)
    }

    if (!editorElement) return

    // Add copy event listener to editor element
    editorElement.addEventListener('copy', handleCopy)

    // Make sure any existing editor is destroyed first
    if (editor) {
      editor.destroy()
      editor = null
    }
    
    // Store original content with YAML
    if (content && content.includes('---\n')) {
      originalContentWithYaml = content
    }
    
    // Store initial content
    lastContent = content
    
    // Remove YAML for editing and process image paths
    let contentWithoutYaml = removeYamlFrontmatter(content)
    contentWithoutYaml = processImagePaths(contentWithoutYaml)
    // Process footnotes for better display
    contentWithoutYaml = processFootnotesForDisplay(contentWithoutYaml)

    // Encode Korean/special character paths for markdown-it parsing
    const { encoded: encodedContent, map: imagePathMap } = encodeImagePaths(contentWithoutYaml)
    contentWithoutYaml = encodedContent
    
    // Create a flag to track initial content loading for this specific editor instance
    let isInitialContentLoad = true

    // Custom extension for generation selection highlight - create fresh instance
    const GenerationSelectionHighlight = Extension.create({
      name: 'generationSelectionHighlight',

      addProseMirrorPlugins() {
        const pluginKey = new PluginKey('generationSelectionHighlight')
        
        return [
          new Plugin({
            key: pluginKey,
            state: {
              init: () => ({ decorations: DecorationSet.empty }),
              apply(tr, value) {
                const meta = tr.getMeta('generationSelectionHighlight')
                
                if (meta?.action === 'set' && meta.from !== undefined && meta.to !== undefined) {
                  const decoration = Decoration.inline(meta.from, meta.to, {
                    class: 'generation-selection-highlight'
                  })
                  return { decorations: DecorationSet.create(tr.doc, [decoration]) }
                } else if (meta?.action === 'clear') {
                  return { decorations: DecorationSet.empty }
                }
                
                // Map existing decorations through the transaction
                return { decorations: value.decorations.map(tr.mapping, tr.doc) }
              }
            },
            props: {
              decorations(state) {
                return this.getState(state)?.decorations
              }
            }
          })
        ]
      }
    })
    
    // Custom extension to prevent initial content from being added to history
    const PreventInitialHistory = Extension.create({
      name: 'preventInitialHistory',
      
      addProseMirrorPlugins() {
        let hasUserInteracted = false
        
        return [
          new Plugin({
            key: new PluginKey('preventInitialHistory'),
            
            filterTransaction: (transaction, state) => {
              // Check if this is a user-initiated change (typing, paste, etc.)
              // Look for actual input events or paste actions
              const isUserAction = transaction.getMeta('paste') || 
                                  transaction.getMeta('inputType') || // Direct input
                                  (transaction.docChanged && 
                                   transaction.steps.length > 0 && 
                                   transaction.getMeta('addToHistory') !== false) // Has steps and not explicitly prevented
              
              // If user has interacted and initial load period is over, enable history
              if (isUserAction && !isInitialContentLoad) {
                hasUserInteracted = true
              }
              
              // Prevent adding to history if:
              // 1. Still in initial load phase, OR
              // 2. User hasn't interacted yet
              if (isInitialContentLoad || !hasUserInteracted) {
                transaction.setMeta('addToHistory', false)
              }
              
              return true
            },
          })
        ]
      },
      
      onCreate() {
        // After the editor is created, allow future changes to be added to history
        // Use longer delay to ensure initial content and any immediate updates are excluded
        setTimeout(() => {
          isInitialContentLoad = false
          // Don't set hasUserInteracted here - wait for actual user input
        }, 300)
      }
    })
    
    // Paste handler for local image paths and clipboard images
    const LocalImagePasteHandler = Extension.create({
      name: 'localImagePasteHandler',
      
      addProseMirrorPlugins() {
        return [
          new Plugin({
            props: {
              handleDrop(view, event, slice, moved) {
                // Handle image file drop
                const files = event.dataTransfer?.files
                if (!files || files.length === 0) {
                  return false
                }

                // Check if any files are images
                let hasImage = false
                for (let i = 0; i < files.length; i++) {
                  if (files[i].type.indexOf('image') !== -1) {
                    hasImage = true
                    break
                  }
                }

                if (!hasImage) {
                  return false
                }

                // Check if we're in demo mode
                const urlParams = new URLSearchParams(window.location.search)
                const isDemoMode = urlParams.get('demo') === 'true'

                if (isDemoMode) {
                  // Show notification in demo mode
                  event.preventDefault()
                  import('$lib/stores/toast').then(({ toast }) => {
                    toast.warning('데모 모드에서는 이미지 업로드가 지원되지 않습니다. 샘플 이미지로 테스트해보세요.', 5000)
                  })
                  return true
                }

                // Prevent default handling for image drops
                event.preventDefault()
                event.stopPropagation()

                // Get drop position once
                const dropPos = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY
                })?.pos || view.state.selection.to

                // Process each image file
                for (let i = 0; i < files.length; i++) {
                  const file = files[i]
                  if (file.type.indexOf('image') !== -1) {
                    const reader = new FileReader()

                    reader.onload = async function(e) {
                      if (e.target?.result && typeof e.target.result === 'string') {
                        try {
                          // Check if we're in Electron environment
                          if ((window as any).electronAPI?.saveImage) {
                            // Extract base64 data (remove data:image/...;base64, prefix)
                            const base64Data = e.target.result.split(',')[1]
                            if (!base64Data) {
                              throw new Error('Failed to extract base64 data from image')
                            }

                            // Use original filename
                            const fileName = file.name || `image_${Date.now()}.png`

                            const result = await (window as any).electronAPI.saveImage({
                              base64: base64Data,
                              fileName: fileName
                            })

                            if (result.success && result.path) {
                              // Insert image at drop position
                              const imageNode = view.state.schema.nodes.image.create({
                                src: result.path,
                                alt: fileName.replace(/\.[^/.]+$/, ''),
                                title: fileName
                              })

                              const transaction = view.state.tr.insert(dropPos, imageNode)
                              view.dispatch(transaction)

                              // Show success message
                              window.dispatchEvent(new CustomEvent('status-message', {
                                detail: { message: '이미지가 추가되었습니다', type: 'success' }
                              }))
                            } else {
                              console.error('Failed to save image:', result)
                              window.dispatchEvent(new CustomEvent('status-message', {
                                detail: { message: '이미지 저장에 실패했습니다', type: 'error' }
                              }))
                            }
                          } else {
                            // If not in Electron, insert as base64
                            const imageNode = view.state.schema.nodes.image.create({
                              src: e.target.result,
                              alt: file.name.replace(/\.[^/.]+$/, ''),
                              title: file.name
                            })

                            const transaction = view.state.tr.insert(dropPos, imageNode)
                            view.dispatch(transaction)
                          }
                        } catch (error) {
                          console.error('Error processing image drop:', error)
                          window.dispatchEvent(new CustomEvent('status-message', {
                            detail: { message: '이미지 처리 중 오류가 발생했습니다', type: 'error' }
                          }))
                        }
                      }
                    }

                    reader.onerror = function(error) {
                      console.error('Error reading file:', error)
                      window.dispatchEvent(new CustomEvent('status-message', {
                        detail: { message: '파일 읽기 중 오류가 발생했습니다', type: 'error' }
                      }))
                    }

                    reader.readAsDataURL(file)
                  }
                }

                return true // Handled
              },

              handlePaste(view, event) {
                // Check for image data in clipboard first
                const items = event.clipboardData?.items
                if (items) {
                  for (let i = 0; i < items.length; i++) {
                    const item = items[i]
                    if (item.type.indexOf('image') !== -1) {
                      event.preventDefault()

                      const blob = item.getAsFile()
                      if (blob) {
                        // Check if we're in demo mode
                        const urlParams = new URLSearchParams(window.location.search)
                        const isDemoMode = urlParams.get('demo') === 'true'

                        if (isDemoMode) {
                          // Show notification in demo mode
                          import('$lib/stores/toast').then(({ toast }) => {
                            toast.warning('데모 모드에서는 이미지 붙여넣기가 지원되지 않습니다. 샘플 이미지로 테스트해보세요.', 5000)
                          })
                          return true
                        }

                        // Convert blob to base64 and save
                        const reader = new FileReader()
                        reader.onload = async function(e) {
                          if (e.target?.result && typeof e.target.result === 'string') {
                            try {
                              // Check if we're in Electron environment
                              if ((window as any).electronAPI?.saveImage) {
                                // Use original filename if available, otherwise generate one
                                let fileName: string
                                if (blob.name && blob.name !== 'image.png' && blob.name !== 'blob') {
                                  // Use original filename if it exists and is not generic
                                  fileName = blob.name
                                } else {
                                  // Generate filename with timestamp for screenshots or unnamed images
                                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
                                  const extension = blob.type.split('/')[1] || 'png'
                                  fileName = `pasted_${timestamp}.${extension}`
                                }

                                const result = await (window as any).electronAPI.saveImage({
                                  base64: e.target.result,
                                  fileName: fileName
                                })
                                
                                if (result?.success && result.path) {
                                  // Insert image with relative path
                                  const { from } = view.state.selection
                                  const node = view.state.schema.nodes.image.create({
                                    src: result.path
                                  })
                                  const transaction = view.state.tr.replaceSelectionWith(node)
                                  view.dispatch(transaction)
                                  
                                  // Dispatch event to refresh file explorer
                                  window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
                                }
                              }
                            } catch (error) {
                              console.error('Error saving clipboard image:', error)
                            }
                          }
                        }
                        reader.readAsDataURL(blob)
                        return true
                      }
                    }
                  }
                }
                
                // If not an image, check for text (file path)
                const text = event.clipboardData?.getData('text/plain')
                if (!text) return false
                
                // Check if it's a local file path (Windows or Mac/Linux)
                const isLocalPath = (
                  /^[A-Za-z]:[\\//]/.test(text) ||  // Windows path (C:\...)
                  /^\//.test(text)                    // Unix path (/Users/...)
                )
                
                // Check if it's an image file
                const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i
                const isImagePath = isLocalPath && imageExtensions.test(text)
                
                if (!isImagePath) return false
                
                // Prevent default paste
                event.preventDefault()
                
                // Handle the local image path
                handleLocalImagePath(text, view)
                
                return true
              }
            }
          })
        ]
      }
    })
    
    const extensions = [
      PreventInitialHistory, // Prevent initial content from being added to history (instance-specific)
      GenerationSelectionHighlight, // Add generation selection highlight extension
      LocalImagePasteHandler, // Add local image paste handler
      createKeyboardShortcuts(onBackspaceAtStart), // Add our custom keyboard shortcuts extension with callback
    ]
    
    extensions.push(
      createFormattingShortcuts(), // Add formatting shortcuts extension
      StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6]
          },
          bulletList: {
            keepMarks: true,
            keepAttributes: false
          },
          orderedList: {
            keepMarks: true,
            keepAttributes: false
          },
          // Configure history with proper limits
          history: {
            depth: 100,
            newGroupDelay: 500,
          },
          // Ensure Bold extension is enabled with keyboard shortcuts
          bold: {
            HTMLAttributes: {
              class: 'font-bold'
            }
          },
          // Ensure Italic extension is enabled
          italic: {
            HTMLAttributes: {
              class: 'font-italic'
            }
          },
          // Ensure Strike extension is enabled for strikethrough
          strike: {
            HTMLAttributes: {
              class: 'line-through'
            }
          },
          // Exclude Link from StarterKit since we add it separately
          link: false,
          // Disable hardBreak to prevent Mod-Enter conflict
          hardBreak: false
        }),
        CustomHardBreak, // Add custom HardBreak that only uses Shift+Enter
        Placeholder.configure({
          placeholder: placeholder,
          emptyEditorClass: 'is-editor-empty'
        }),
        // Add Image support with custom rendering for relative paths
        Image.extend({
          // Override parseHTML to handle all image sources properly
          parseHTML() {
            return [
              {
                tag: 'img[src]',
                getAttrs: (dom) => {
                  if (typeof dom === 'string') return false
                  const element = dom as HTMLElement

                  return {
                    src: element.getAttribute('src'),
                    alt: element.getAttribute('alt') || '',
                    title: element.getAttribute('title') || null
                  }
                }
              }
            ]
          },

          addNodeView() {
            return ({ node, HTMLAttributes }) => {
              const container = document.createElement('div')
              container.className = 'image-wrapper'

              const img = document.createElement('img')
              const vault = get(vaultStore)
              let src = node.attrs.src
              const originalSrc = node.attrs.src

              // Skip loading if this is a placeholder (will be replaced soon)
              if (src && src.startsWith('__IMAGE_PLACEHOLDER_')) {
                // Set a blank image temporarily
                img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3C/svg%3E'
                container.appendChild(img)

                return {
                  dom: container,
                  update: (updatedNode) => {
                    // When node is updated with real path, reload image
                    if (updatedNode.type.name === 'image') {
                      const newSrc = updatedNode.attrs.src
                      if (newSrc && !newSrc.startsWith('__IMAGE_PLACEHOLDER_')) {
                        // Trigger a re-render by returning false
                        return false
                      }
                    }
                    return true
                  }
                }
              }
              const isElectron = typeof window !== 'undefined' && ((window as any).electronAPI || (window as any).api)
              const isDemoMode = vault?.kind === 'demo' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true')

              // Track subscription for cleanup
              let vaultSubscription: (() => void) | null = null

              // Handle image paths based on mode
              if (src && !src.startsWith('http') && !src.startsWith('file://') && !src.startsWith('data:')) {
                // In Electron, if vault is not yet initialized, subscribe to vaultStore updates
                if (isElectron && !vault?.vaultPath && !isDemoMode) {
                  // Vault not ready yet - subscribe to vaultStore and update when ready
                  vaultSubscription = vaultStore.subscribe((updatedVault) => {
                    if (updatedVault?.vaultPath) {
                      // Decode URL-encoded paths
                      let decodedSrc = originalSrc
                      try {
                        decodedSrc = decodeURIComponent(originalSrc)
                      } catch (e) {
                        console.warn('[TipTap Image] Failed to decode src:', originalSrc)
                      }

                      const fullPath = `${updatedVault.vaultPath}/${decodedSrc}`
                      img.src = `file://${encodeURI(fullPath).replace(/#/g, '%23')}`

                      // Unsubscribe after first successful update
                      if (vaultSubscription) {
                        vaultSubscription()
                        vaultSubscription = null
                      }
                    }
                  })
                  // Show placeholder initially
                  img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f0f0f0"/%3E%3C/svg%3E'
                } else if (isDemoMode && !isElectron) {
                  // In demo mode (browser only), use Vite glob to load images from templates
                  let imagePath = src

                  // Decode URL-encoded paths (한글 characters)
                  try {
                    imagePath = decodeURIComponent(imagePath)
                  } catch (e) {
                    // If decoding fails, use as-is
                    console.warn('[TipTap Image] Failed to decode path:', imagePath)
                  }

                  // Remove ./ or ../ prefix
                  if (imagePath.startsWith('./')) {
                    imagePath = imagePath.replace(/^\.\//, '')
                  } else if (imagePath.startsWith('../')) {
                    imagePath = imagePath.replace(/^\.\.\//, '')
                  }

                  // Build full path for glob lookup
                  const fullPath = `/src/lib/templates/${imagePath}`
                  let imageData = imageModules[fullPath]

                  // If not found, try normalizing (case-insensitive match)
                  if (!imageData) {
                    const normalizedSearchPath = fullPath.toLowerCase()
                    const matchingKey = Object.keys(imageModules).find(key =>
                      key.toLowerCase() === normalizedSearchPath
                    )
                    if (matchingKey) {
                      imageData = imageModules[matchingKey]
                    }
                  }

                  if (imageData) {
                    src = imageData as string
                  } else {
                    console.error('[TipTap Image] Image not found in templates:', imagePath)
                    console.error('[TipTap Image] Full path:', fullPath)
                    console.error('[TipTap Image] Available:', Object.keys(imageModules))
                  }
                } else if (vault?.vaultPath) {
                  // Convert relative paths to absolute file:// paths for Electron mode
                  // Decode URL-encoded paths first
                  let decodedSrc = src
                  try {
                    decodedSrc = decodeURIComponent(src)
                  } catch (e) {
                    console.warn('[TipTap Image] Failed to decode src:', src)
                  }

                  const fullPath = `${vault.vaultPath}/${decodedSrc}`
                  // Use encodeURI to handle spaces while preserving path structure
                  src = `file://${encodeURI(fullPath).replace(/#/g, '%23')}`
                }
              }

              img.src = src
              img.className = 'tiptap-image'
              img.alt = node.attrs.alt || ''
              img.title = node.attrs.title || ''

              // Create placeholder element (hidden initially)
              const placeholder = document.createElement('div')
              placeholder.className = 'image-error-placeholder'
              placeholder.style.display = 'none'
              placeholder.style.padding = '2em'
              placeholder.style.margin = '1em 0'
              placeholder.style.background = 'var(--bg-secondary)'
              placeholder.style.border = '2px dashed var(--border-secondary)'
              placeholder.style.borderRadius = '8px'
              placeholder.style.cursor = 'pointer'
              placeholder.style.textAlign = 'center'

              const fileName = originalSrc.split('/').pop() || 'image'
              placeholder.innerHTML = `
                <div class="image-error-content" style="color: var(--text-secondary); font-size: 0.9em;">
                  <span class="image-error-filename" style="color: var(--text-primary); font-weight: 500; font-family: 'SF Mono', Monaco, monospace; background: var(--bg-primary); padding: 0.2em 0.4em; border-radius: 3px;">"${fileName}"</span> could not be found.
                </div>
                <div class="image-markdown-source" style="display:none; margin-top: 1em; padding: 1em; background: var(--bg-primary); border-radius: 4px; font-family: monospace; font-size: 0.85em; color: var(--text-secondary);">
                  ![${img.alt}](${originalSrc})
                </div>
              `

              // Add hover effect
              placeholder.onmouseenter = function() {
                this.style.background = 'var(--bg-tertiary)'
                this.style.borderColor = 'var(--border-primary)'
              }

              placeholder.onmouseleave = function() {
                this.style.background = 'var(--bg-secondary)'
                this.style.borderColor = 'var(--border-secondary)'
              }

              placeholder.onclick = function() {
                const mdSource = this.querySelector('.image-markdown-source')
                if (mdSource) {
                  mdSource.style.display = mdSource.style.display === 'none' ? 'block' : 'none'
                }
              }

              // Handle load/error events
              img.onerror = () => {
                img.style.display = 'none'
                placeholder.style.display = 'block'
              }

              img.onload = () => {
                img.style.display = ''
                placeholder.style.display = 'none'
              }

              // Create menu container
              const menuContainer = document.createElement('div')
              menuContainer.style.position = 'absolute'
              menuContainer.style.top = '0'
              menuContainer.style.right = '0'
              menuContainer.style.zIndex = '10'

              // Mount ImageSizeMenu component
              try {
                mount(ImageSizeMenu, {
                  target: menuContainer,
                  props: {
                    imagePath: originalSrc,
                    onResizeComplete: () => {
                      // Force image reload by updating the src
                      const currentSrc = img.src
                      img.src = ''
                      setTimeout(() => {
                        img.src = currentSrc + '?t=' + Date.now()
                      }, 100)
                    },
                    onDelete: () => {
                      // Find and delete this image node from the editor
                      if (!editor) return

                      const { state } = editor
                      const { doc } = state
                      let imagePos = -1

                      // Find the position of this image node
                      doc.descendants((node, pos) => {
                        if (node.type.name === 'image' && node.attrs.src === originalSrc) {
                          imagePos = pos
                          return false // Stop searching
                        }
                      })

                      if (imagePos >= 0) {
                        // Delete the image node
                        const tr = state.tr.delete(imagePos, imagePos + 1)
                        editor.view.dispatch(tr)

                        // Show success message
                        window.dispatchEvent(new CustomEvent('status-message', {
                          detail: {
                            message: '이미지가 제거되었습니다',
                            type: 'success'
                          }
                        }))
                      }
                    }
                  }
                })
              } catch (err) {
                console.error('Failed to mount ImageSizeMenu:', err)
              }

              container.appendChild(img)
              container.appendChild(placeholder)
              container.appendChild(menuContainer)

              return {
                dom: container,
                update: (updatedNode) => {
                  if (updatedNode.type.name !== 'image') return false

                  const newSrc = updatedNode.attrs.src
                  const forceUpdateChanged = updatedNode.attrs._forceUpdate !== node.attrs._forceUpdate

                  // Always update src if _forceUpdate changed (for deletion detection)
                  if (newSrc !== node.attrs.src || forceUpdateChanged) {
                    let processedSrc = newSrc
                    if (newSrc && !newSrc.startsWith('http') && !newSrc.startsWith('file://') && !newSrc.startsWith('data:')) {
                      if (vault?.vaultPath) {
                        // Convert relative paths to absolute file:// paths for Electron mode
                        // Decode URL-encoded paths first
                        let decodedSrc = newSrc
                        try {
                          decodedSrc = decodeURIComponent(newSrc)
                        } catch (e) {
                          console.warn('[TipTap Image Update] Failed to decode src:', newSrc)
                        }

                        const fullPath = `${vault.vaultPath}/${decodedSrc}`
                        // Use encodeURI to handle spaces while preserving path structure
                        processedSrc = `file://${encodeURI(fullPath).replace(/#/g, '%23')}`
                      }
                    }

                    // Add timestamp to bust cache and force reload (important for deletion detection)
                    const cacheBuster = `?t=${Date.now()}`
                    const srcWithCache = processedSrc + cacheBuster

                    // Force re-check by resetting src
                    img.src = ''
                    setTimeout(() => {
                      img.src = srcWithCache
                    }, 0)

                    const newFileName = newSrc.split('/').pop() || 'image'
                    const errorFilename = placeholder.querySelector('.image-error-filename')
                    const markdownSource = placeholder.querySelector('.image-markdown-source')
                    if (errorFilename) {
                      errorFilename.textContent = `"${newFileName}"`
                    }
                    if (markdownSource) {
                      markdownSource.textContent = `![${updatedNode.attrs.alt || ''}](${newSrc})`
                    }
                  }

                  img.alt = updatedNode.attrs.alt || ''
                  img.title = updatedNode.attrs.title || ''
                  node = updatedNode

                  return true
                },
                destroy: () => {
                  // Clean up vault store subscription if still active
                  if (vaultSubscription) {
                    vaultSubscription()
                    vaultSubscription = null
                  }

                  // Svelte 5: Components are automatically cleaned up when removed from DOM
                  // No need to manually destroy - just ensure DOM cleanup happens
                  if (container.parentNode) {
                    container.remove()
                  }
                }
              }
            }
          }
        }).configure({
          inline: true,
          allowBase64: true,
        }),
        // Add Link support with YouTube URL filtering
        Link.configure({
          openOnClick: false, // Don't open links in editor
          HTMLAttributes: {
            class: 'tiptap-link',
            rel: 'noopener noreferrer',
            target: null // Don't set target in editor
          },
          validate: href => {
            // Don't create link nodes for standalone YouTube URLs
            // They should be converted to YouTube embeds instead
            const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(\S*)?$/
            if (youtubeRegex.test(href)) {
              return false // Prevent YouTube URLs from becoming links
            }
            return true
          }
        }),
        // Add YouTube support with custom node view
        Youtube.extend({
          addNodeView() {
            return ({ node }) => {
              const container = document.createElement('div')
              container.className = 'tiptap-youtube'
              container.style.display = 'flex'
              container.style.justifyContent = 'center'
              container.style.margin = '2em 0'
              container.style.position = 'relative'

              // Extract video ID from the URL
              const src = node.attrs.src
              let videoId = ''
              let updateOnlineStatus // Define here for cleanup function access

              if (src.includes('youtube.com/watch?v=')) {
                videoId = src.split('v=')[1]?.split('&')[0] || ''
              } else if (src.includes('youtu.be/')) {
                videoId = src.split('youtu.be/')[1]?.split('?')[0] || ''
              }

              if (videoId) {
                // Create wrapper for iframe and offline state
                const wrapper = document.createElement('div')
                wrapper.style.position = 'relative'
                wrapper.style.width = '100%'
                wrapper.style.maxWidth = node.attrs.width || '640px'
                wrapper.style.aspectRatio = '16 / 9'

                const iframe = document.createElement('iframe')
                iframe.src = getYouTubeEmbedUrl(videoId)
                iframe.width = '100%'
                iframe.height = '100%'
                iframe.setAttribute('frameborder', '0')
                iframe.setAttribute('allowfullscreen', 'true')
                iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share')
                iframe.setAttribute('referrerPolicy', 'strict-origin-when-cross-origin')
                iframe.style.borderRadius = '8px'
                iframe.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
                iframe.style.display = 'block'

                // Create offline placeholder
                const offlinePlaceholder = document.createElement('div')
                offlinePlaceholder.className = 'youtube-offline-placeholder'
                offlinePlaceholder.style.position = 'absolute'
                offlinePlaceholder.style.top = '0'
                offlinePlaceholder.style.left = '0'
                offlinePlaceholder.style.width = '100%'
                offlinePlaceholder.style.height = '100%'
                offlinePlaceholder.style.display = 'none'
                offlinePlaceholder.style.flexDirection = 'column'
                offlinePlaceholder.style.alignItems = 'center'
                offlinePlaceholder.style.justifyContent = 'center'
                offlinePlaceholder.style.background = 'var(--bg-secondary)'
                offlinePlaceholder.style.border = '2px solid var(--border-secondary)'
                offlinePlaceholder.style.borderRadius = '8px'
                offlinePlaceholder.style.color = 'var(--text-secondary)'
                offlinePlaceholder.style.textAlign = 'center'
                offlinePlaceholder.style.padding = '2em'

                offlinePlaceholder.innerHTML = `
                  <div style="font-size: 48px; margin-bottom: 0.5em; opacity: 0.5;">📺</div>
                  <div style="font-weight: 500; margin-bottom: 0.5em;">YouTube 비디오 (오프라인)</div>
                  <div style="font-size: 0.9em; opacity: 0.8; margin-bottom: 1em;">인터넷 연결이 필요합니다</div>
                  <a href="${src}" target="_blank" style="color: var(--accent-color); text-decoration: none; font-size: 0.85em;">
                    ${src}
                  </a>
                `

                // Check online status and handle iframe load errors
                updateOnlineStatus = function() {
                  if (!navigator.onLine) {
                    iframe.style.display = 'none'
                    offlinePlaceholder.style.display = 'flex'
                  } else {
                    iframe.style.display = 'block'
                    offlinePlaceholder.style.display = 'none'
                  }
                }

                // Initial check
                updateOnlineStatus()

                // Listen for online/offline events
                window.addEventListener('online', updateOnlineStatus)
                window.addEventListener('offline', updateOnlineStatus)

                // Handle iframe load errors
                iframe.onerror = () => {
                  iframe.style.display = 'none'
                  offlinePlaceholder.style.display = 'flex'
                }

                wrapper.appendChild(iframe)
                wrapper.appendChild(offlinePlaceholder)
                container.appendChild(wrapper)
              } else {
                // Fallback for invalid URLs
                const placeholder = document.createElement('div')
                placeholder.textContent = '⚠️ Invalid YouTube URL'
                placeholder.style.padding = '2em'
                placeholder.style.background = 'var(--bg-secondary)'
                placeholder.style.border = '1px solid var(--border-primary)'
                placeholder.style.borderRadius = '8px'
                placeholder.style.color = 'var(--text-secondary)'
                container.appendChild(placeholder)
              }

              // Store cleanup function
              const cleanup = () => {
                if (updateOnlineStatus) {
                  window.removeEventListener('online', updateOnlineStatus)
                  window.removeEventListener('offline', updateOnlineStatus)
                }
              }

              return {
                dom: container,
                update: (updatedNode) => {
                  if (updatedNode.type.name !== 'youtube') return false

                  const newSrc = updatedNode.attrs.src
                  if (newSrc !== node.attrs.src) {
                    let newVideoId = ''

                    if (newSrc.includes('youtube.com/watch?v=')) {
                      newVideoId = newSrc.split('v=')[1]?.split('&')[0] || ''
                    } else if (newSrc.includes('youtu.be/')) {
                      newVideoId = newSrc.split('youtu.be/')[1]?.split('?')[0] || ''
                    }

                    if (newVideoId) {
                      const wrapper = container.querySelector('div')
                      const iframe = wrapper?.querySelector('iframe')
                      const placeholder = wrapper?.querySelector('.youtube-offline-placeholder')
                      if (iframe) {
                        iframe.src = getYouTubeEmbedUrl(newVideoId)
                      }
                      if (placeholder) {
                        const link = placeholder.querySelector('a')
                        if (link) {
                          link.href = newSrc
                          link.textContent = newSrc
                        }
                      }
                    }
                  }

                  node = updatedNode
                  return true
                },
                destroy: cleanup
              }
            }
          }
        }).configure({
          width: 640,
          height: 480,
          nocookie: true,
          autoplay: false,
          ccLanguage: 'en',
          HTMLAttributes: {
            class: 'youtube-embed',
          }
        }),
        // Add Markdown extension for proper markdown support
        Markdown.configure({
          html: false,
          transformCopiedText: false,  // Disable automatic markdown transformation on copy
          transformPastedText: true,   // Enable markdown transformation on paste for images
          linkify: false,              // Disable auto-linking to prevent URL encoding issues
          breaks: false                // Preserve markdown line breaks behavior
        }),
        // Add search highlighting extension
        SearchHighlight.configure({
          searchTerm: '',
          highlightClass: 'search-highlight',
          currentClass: 'search-current',
          caseSensitive: false
        }),
        // Add inline view highlighting extension
        InlineViewHighlight.configure({
          highlightClass: 'inline-view-highlight',
          onRangeHover: (viewId: string | null) => {
            if (viewId === lastInlineHoverId) {
              return
            }

            lastInlineHoverId = viewId

            if (viewId) {
              // Dispatch event to highlight the corresponding card
              window.dispatchEvent(new CustomEvent('inline-decoration-hover', {
                detail: { viewId }
              }))
            } else {
              // Clear hover
              window.dispatchEvent(new CustomEvent('inline-decoration-hover-end'))
            }
          },
          onRangeClick: (from: number, to: number) => {
            // Calculate YAML offset to adjust positions back
            let yamlOffset = 0
            if (originalContentWithYaml && originalContentWithYaml.includes('---\n')) {
              const yamlMatch = originalContentWithYaml.match(/^---\n[\s\S]*?\n---\n/)
              if (yamlMatch) {
                yamlOffset = yamlMatch[0].length
              }
            }

            // Adjust positions to match the original text with YAML
            const originalFrom = from + yamlOffset
            const originalTo = to + yamlOffset

            // Find the inline view for this range
            const views = get(inlineViewStore).views
            const view = views.find(v =>
              v.position.start === originalFrom && v.position.end === originalTo
            )

            if (view) {
              // Scroll to the corresponding card in the sidebar
              const cardElement = document.querySelector(
                `[data-segment-id="${view.id}"]`
              ) as HTMLElement

              if (cardElement) {
                const container = cardElement.closest('.inline-view-cards-list, .errors-list') as HTMLElement
                if (container) {
                  const containerRect = container.getBoundingClientRect()
                  const cardRect = cardElement.getBoundingClientRect()
                  const relativeTop = cardElement.offsetTop

                  // Smooth scroll to center the card in view
                  const targetScroll = relativeTop - (containerRect.height / 2) + (cardRect.height / 2)

                  container.scrollTo({
                    top: Math.max(0, targetScroll),
                    behavior: 'smooth'
                  })

                  // Add a highlight effect to the card
                  cardElement.classList.add('highlight-flash')
                  setTimeout(() => {
                    cardElement.classList.remove('highlight-flash')
                  }, 1000)
                }
              }
            }
          },
          onRangeModified: (viewId: string) => {
            // Cancel the inline view if its highlighted text was modified
            if (viewId) {
              inlineViewStore.cancelView(viewId)
            }
          }
        }),
        // Add Table extensions
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: 'tiptap-table'
          }
        }),
        TableRow,
        TableHeader,
        TableCell
    )
    
    editor = new Editor({
      element: editorElement,
      extensions,
      content: contentWithoutYaml,
      editable: !readonly && !disabled,
      autofocus: true,
      shouldRerenderOnTransaction: false,
      editorProps: {
        attributes: {
          class: 'tiptap-editor-content',
          role: 'textbox',
          'aria-label': '문서 본문',
          'aria-multiline': 'true',
          spellcheck: 'false',
          'data-gramm': 'false',
          'data-gramm_editor': 'false',
          'data-enable-grammarly': 'false'
        },
        // Custom clipboard text serializer to copy as markdown
        clipboardTextSerializer: (slice) => {
          // Get markdown serializer from the editor
          const markdownSerializer = editor?.storage.markdown?.serializer
          if (markdownSerializer) {
            try {
              // Convert the slice to markdown
              return markdownSerializer.serialize(slice.content)
            } catch (error) {
              console.error('[TipTap] Failed to serialize clipboard content as markdown:', error)
            }
          }
          // Fallback to default text serialization
          return slice.content.textBetween(0, slice.content.size, '\n\n')
        },
        // Handle image drop from FileExplorer
        handleDrop: (view, event: DragEvent, slice, moved) => {
          // Check if this is an image file from FileExplorer
          const imageData = event.dataTransfer?.getData('application/x-image-file')

          if (!imageData) {
            return false // Let TipTap handle other drops
          }

          try {
            const { path, name } = JSON.parse(imageData)

            // Get vault path for relative path calculation
            const vault = get(vaultStore)
            const vaultPath = vault?.vaultPath || ''

            // Convert to vault-relative path
            const relativePath = getVaultRelativePath(path, vaultPath)

            // Insert image node at drop position
            const { schema } = view.state
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })

            if (coordinates) {
              // Create image node with relative path
              const imageNode = schema.nodes.image.create({
                src: relativePath,
                alt: ''
              })

              const transaction = view.state.tr.insert(coordinates.pos, imageNode)
              view.dispatch(transaction)
            }

            return true // Prevent default handling
          } catch (error) {
            console.error('Failed to insert image from drop:', error)
            return false
          }
        }
      },
      onCreate: ({ editor }) => {
        // Register custom serializers in tiptap-markdown
        const markdownStorage = editor.storage.markdown
        if (markdownStorage && markdownStorage.serializer && markdownStorage.serializer.nodes) {
          // Override the YouTube node serializer to output URL instead of [youtube]
          markdownStorage.serializer.nodes.youtube = (state: any, node: any) => {
            const url = node.attrs.src || ''
            if (url) {
              state.write(url)
              state.closeBlock(node)
            }
          }

          // Add Image node serializer to properly output markdown image syntax
          markdownStorage.serializer.nodes.image = (state: any, node: any) => {
            const src = node.attrs.src || ''
            const alt = node.attrs.alt || ''
            const title = node.attrs.title || ''

            if (src) {
              // Format: ![alt](src "title") or ![alt](src)
              if (title) {
                state.write(`![${alt}](${src} "${title}")`)
              } else {
                state.write(`![${alt}](${src})`)
              }
              // Image is inline, don't call closeBlock
            }
          }
        }

        // Decode Korean image paths immediately (synchronously) to avoid image load errors
        if (imagePathMap.size > 0) {
          // Use queueMicrotask to decode after view is created but before rendering
          queueMicrotask(() => {
            decodeImagePaths(editor, imagePathMap)
          })
        }

        // Auto-detect and convert standalone YouTube URLs to embeds on initial load
        setTimeout(() => {
          if (editor && !editor.isDestroyed) {
            const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(\S*)?$/
            const doc = editor.state.doc

            // Collect all YouTube URLs to convert (with their positions)
            const conversions: Array<{ pos: number; nodeSize: number; url: string }> = []

            doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && node.childCount === 1) {
                const child = node.firstChild
                if (child && child.type.name === 'text') {
                  const text = child.text?.trim() || ''
                  if (youtubeRegex.test(text)) {
                    // Check if youtube node type exists
                    if (editor.schema.nodes.youtube) {
                      conversions.push({
                        pos,
                        nodeSize: node.nodeSize,
                        url: text
                      })
                    }
                  }
                }
              }
            })

            // Process conversions in reverse order to maintain valid positions
            if (conversions.length > 0) {
              let tr = editor.state.tr

              // Sort by position descending (process from end to start)
              conversions.sort((a, b) => b.pos - a.pos)

              for (const { pos, nodeSize, url } of conversions) {
                tr.replaceRangeWith(
                  pos,
                  pos + nodeSize,
                  editor.schema.nodes.youtube.create({ src: url })
                )
              }

              editor.view.dispatch(tr)
            }
          }
        }, 200) // Give time for editor to fully initialize
      },
      onUpdate: ({ editor }) => {
        if (isUpdating) return

        characterCount = editor.state.doc.textContent.length
        scheduleContentUpdate()
      },
      onSelectionUpdate: ({ editor }) => {
        // Notify parent component about selection changes
        if (onSelectionChange) {
          onSelectionChange()
        }
      }
    })
    
    // Set initial character count
    characterCount = editor.state.doc.textContent.length
    
    // Create MarkdownRenderer instance
    markdownRenderer = new MarkdownRenderer(editor)
    
    setupLinkPreviewHandlers()
  })
  
  // Update editor when content changes from outside
  $effect(() => {
    // Skip if we're the ones updating
    if (isUpdating || !editor || editor.isDestroyed) {
      return
    }
    
    // Check if content actually changed
    if (content !== lastContent) {
      cancelScheduledContentUpdate()

      let contentWithoutYaml = removeYamlFrontmatter(content)
      contentWithoutYaml = processImagePaths(contentWithoutYaml)
      // Process footnotes for better display
      contentWithoutYaml = processFootnotesForDisplay(contentWithoutYaml)

      // Encode Korean/special character paths for markdown-it parsing
      const { encoded: encodedContent, map: imagePathMap } = encodeImagePaths(contentWithoutYaml)

      // Use setContent with markdown directly, but don't add to history
      isUpdating = true
      editor.chain()
        .setContent(encodedContent)
        .command(({ tr }) => {
          tr.setMeta('addToHistory', false)
          return true
        })
        .run()

      // Fix markdown link parsing issue in list items (tiptap-markdown bug)
      // When list items contain only a link, tiptap-markdown fails to parse them
      setTimeout(() => {
        const tr = editor.state.tr
        let offset = 0

        editor.state.doc.descendants((node: any, pos: number) => {
          // Find text nodes that contain markdown link syntax
          if (node.isText && node.text && /\[([^\]]+)\]\(([^)]+)\)/.test(node.text)) {
            // Parse all markdown links in this text node
            const text = node.text
            const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g
            let match

            const fragments: any[] = []
            let lastIndex = 0

            while ((match = linkPattern.exec(text)) !== null) {
              const fullMatch = match[0]
              const linkText = match[1]
              const url = match[2].replace(/\\b/g, '') // Remove \b escapes

              // Add text before the link (if any)
              if (match.index > lastIndex) {
                fragments.push(editor.schema.text(text.substring(lastIndex, match.index), node.marks))
              }

              // Add the link
              const linkMark = editor.schema.marks.link.create({
                href: url,
                target: null,
                rel: 'noopener noreferrer',
                class: 'tiptap-link'
              })
              fragments.push(editor.schema.text(linkText, [linkMark, ...node.marks]))

              lastIndex = match.index + fullMatch.length
            }

            // Add remaining text after last link (if any)
            if (lastIndex < text.length) {
              fragments.push(editor.schema.text(text.substring(lastIndex), node.marks))
            }

            // Replace the node
            if (fragments.length > 0) {
              tr.replaceWith(pos + offset, pos + offset + node.nodeSize, fragments)
              offset += fragments.reduce((sum, f) => sum + f.nodeSize, 0) - node.nodeSize
            }
          }
        })

        if (offset !== 0) {
          editor.view.dispatch(tr)
        }
      }, 0)

      // Decode Korean image paths after content is loaded
      if (imagePathMap.size > 0) {
        setTimeout(() => {
          decodeImagePaths(editor, imagePathMap)
        }, 0)
      }
      lastContent = content

      // Update original content with YAML
      if (content.includes('---\n')) {
        originalContentWithYaml = content
      } else {
        originalContentWithYaml = ''
      }

      // Reset flag after update
      queueMicrotask(() => {
        isUpdating = false
      })
    }
  })
  
  // Update editor editable state
  $effect(() => {
    if (editor && !editor.isDestroyed) {
      const shouldBeEditable = !readonly && !disabled
      // Only update if state actually changed
      if (editor.isEditable !== shouldBeEditable) {
        editor.setEditable(shouldBeEditable)
      }
    }
  })
  
  // Handle ESC key to close bubble menu and force clear any stuck state
  $effect(() => {
    if (!editor || editor.isDestroyed) return
    
    function handleEscKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && editor && !editor.isDestroyed) {
        // Always try to clear selection and hide bubble menu
        const { empty } = editor.state.selection
        if (!empty) {
          // Clear selection to hide bubble menu - move cursor to end of selection
          editor.commands.setTextSelection(editor.state.selection.to)
        }

      }
    }
    
    // Add event listener
    document.addEventListener('keydown', handleEscKey)
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  })
  
  // Handle click to focus when searching
  function handleEditorClick(e: MouseEvent) {
    if (!editor || editor.isDestroyed) return
    
    // Check if click is on the editor wrapper but not on the content
    const target = e.target as HTMLElement
    const isOnWrapper = target.classList.contains('tiptap-editor-wrapper') || 
                        target.classList.contains('tiptap-editor')
    
    // Always focus editor when clicking, especially for empty documents
    if (isOnWrapper || editor.isEmpty) {
      editor.commands.focus('end')
    }
    
    // Handle search state
    const state = get(searchStore)
    if (state.isVisible) {
      // Focus the editor when user explicitly clicks during search
      editor.commands.focus('end')
    }
  }
  
  // Subscribe to search store for highlighting
  let unsubscribeSearch: (() => void) | null = null
  // Subscribe to inline view store for highlighting
  let unsubscribeInlineView: (() => void) | null = null
  
  $effect(() => {
    // Enable search highlighting always when editor is ready
    if (editor && !editor.isDestroyed) {
      let previousIndex = -1
      unsubscribeSearch = searchStore.subscribe(state => {
        if (!editor || editor.isDestroyed) return
        
        // Update search highlighting
        if (state.searchTerm && state.isVisible) {
          // Find current match index
          const currentIndex = state.currentMatchIndex >= 0 ? state.currentMatchIndex : -1
          editor.commands.setSearchTerm(state.searchTerm, currentIndex)
          
          // Auto-scroll to current match when index changes
          if (currentIndex >= 0 && currentIndex !== previousIndex && state.matches.length > 0) {
            previousIndex = currentIndex
            const currentMatch = state.matches[currentIndex]
            if (currentMatch) {
              // Use setTimeout to ensure DOM has updated
              setTimeout(() => {
                if (!editor || editor.isDestroyed) return
                
                // Find the decorated element and scroll to it
                const decoratedElements = editor.view.dom.querySelectorAll('.search-current')
                if (decoratedElements.length > 0) {
                  decoratedElements[0].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                  })
                } else {
                  // Fallback: use editor's scrollIntoView command
                  editor.commands.scrollIntoView()
                }
              }, 50)
            }
          }
        } else {
          editor.commands.clearSearch()
          previousIndex = -1
        }
      })
    } else {
      if (unsubscribeSearch) {
        unsubscribeSearch()
        unsubscribeSearch = null
      }
    }
    
    // Cleanup on effect re-run or component destroy
    return () => {
      if (unsubscribeSearch) {
        unsubscribeSearch()
        unsubscribeSearch = null
      }
    }
  })
  
  // Subscribe to inline view store for highlighting
  $effect(() => {
    if (editor && !editor.isDestroyed) {
      unsubscribeInlineView = inlineViewStore.subscribe(state => {
        if (!editor || editor.isDestroyed) return
        
        // Get active inline views that should be highlighted
        const activeViews = state.views.filter(v => 
          v.status !== 'applied' && v.status !== 'cancelled' && v.type === 'card'
        )
        
        // Calculate YAML offset to adjust positions
        let yamlOffset = 0
        if (originalContentWithYaml && originalContentWithYaml.includes('---\n')) {
          const yamlMatch = originalContentWithYaml.match(/^---\n[\s\S]*?\n---\n/)
          if (yamlMatch) {
            yamlOffset = yamlMatch[0].length
          }
        }
        
        // Convert positions to ProseMirror ranges (subtract YAML offset)
        const ranges = activeViews.map(view => ({
          from: Math.max(0, view.position.start - yamlOffset),
          to: Math.max(0, view.position.end - yamlOffset),
          id: view.id
        }))
        
        // Update highlights
        editor.commands.setInlineViewRanges(ranges)
      })
    } else {
      if (unsubscribeInlineView) {
        unsubscribeInlineView()
        unsubscribeInlineView = null
      }
    }
    
    // Cleanup on effect re-run or component destroy
    return () => {
      if (unsubscribeInlineView) {
        unsubscribeInlineView()
        unsubscribeInlineView = null
      }
    }
  })
  
  // Perform search when content changes or search term changes
  $effect(() => {
    if (editor && !editor.isDestroyed) {
      const state = get(searchStore)
      if (state.searchTerm && state.isVisible) {
        // Get plain text from editor for search (already has YAML removed)
        const text = editor.getText()
        // Perform search on the text without YAML
        const matches = searchStore.search(text, state.searchTerm)
        
        // Update highlighting for all matches
        if (matches && matches.length > 0) {
          // The SearchHighlight extension will handle highlighting based on searchStore state
          editor.commands.setSearchTerm(state.searchTerm, state.currentMatchIndex)
        }
      }
    }
  })
  
  
  // Listen for image file deletion events
  $effect(() => {
    function handleImageDeleted(event: CustomEvent) {
      if (!editor || editor.isDestroyed) return
      
      const { filePath } = event.detail
      
      // Force all images to re-check their sources
      // This will trigger the error handler for deleted images
      const images = editor.$nodes('image')
      if (images && images.length > 0) {
        // Get all image nodes
        editor.state.doc.descendants((node, pos) => {
          if (node.type.name === 'image') {
            const src = node.attrs.src
            // Check if this image references the deleted file
            if (src && (src === filePath || src.endsWith(filePath) || filePath.endsWith(src))) {
              // Force a re-render by updating the node
              // This will trigger the image error handler in the node view
              const transaction = editor.state.tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                // Add a timestamp to force re-render
                _forceUpdate: Date.now()
              })
              editor.view.dispatch(transaction)
            }
          }
        })
      }
    }
    
    window.addEventListener('image-file-deleted', handleImageDeleted)
    return () => window.removeEventListener('image-file-deleted', handleImageDeleted)
  })
  
  // Listen for scrollToCurrentMatch events
  $effect(() => {
    function handleScrollToCurrentMatch() {
      if (!editor || editor.isDestroyed) return
      
      const state = get(searchStore)
      const currentMatch = state.matches[state.currentMatchIndex]
      if (!currentMatch) return
      
      // Since we're already searching on YAML-removed text,
      // we need to find the match in the ProseMirror document directly
      const searchTerm = state.searchTerm
      if (!searchTerm) return
      
      const doc = editor.state.doc
      const textContent = doc.textContent
      
      // Find all matches in the document
      const regex = new RegExp(
        searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'gi'
      )
      
      let matches = []
      let match
      while ((match = regex.exec(textContent)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length
        })
      }
      
      // Get the current match based on index
      if (state.currentMatchIndex >= 0 && state.currentMatchIndex < matches.length) {
        const targetMatch = matches[state.currentMatchIndex]
        
        // Find the position in the ProseMirror document
        let textPos = 0
        let found = false
        
        doc.descendants((node, pos) => {
          if (found) return false // Stop traversing
          
          if (node.isText) {
            const nodeText = node.text || ''
            const nodeStart = textPos
            const nodeEnd = textPos + nodeText.length
            
            // Check if target match starts in this node
            if (targetMatch.start >= nodeStart && targetMatch.start < nodeEnd) {
              const offsetInNode = targetMatch.start - nodeStart
              const absolutePos = pos + offsetInNode
              const absoluteEndPos = absolutePos + (targetMatch.end - targetMatch.start)
              
              // Don't set text selection - let the SearchHighlight extension handle visual highlighting
              // Just scroll to the match position
              setTimeout(() => {
                // Calculate the DOM position for scrolling
                const domAtPos = editor.view.domAtPos(absolutePos)
                if (domAtPos && domAtPos.node) {
                  const element = domAtPos.node.parentElement || domAtPos.node
                  element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                  })
                }
              }, 50)
              
              found = true
              return false // Stop traversing
            }
            
            textPos += nodeText.length
          } else if (node.isBlock && textPos > 0) {
            // Add 1 for block boundaries (newlines)
            textPos += 1
          }
        })
      }
    }
    
    window.addEventListener('scrollToCurrentMatch', handleScrollToCurrentMatch)
    return () => window.removeEventListener('scrollToCurrentMatch', handleScrollToCurrentMatch)
  })

  // Handle inline view hover events from sidebar cards
  let hoverDebounceTimer: number | null = null
  let currentHoveredViewId: string | null = null
  let autoRemoveTimer: number | null = null

  $effect(() => {
    if (!editor) return

    const handleInlineViewHover = (event: CustomEvent) => {
      if (!editor) return

      const detail = event.detail
      if (!detail || !detail.viewId) return

      // Clear any existing timers
      if (hoverDebounceTimer !== null) {
        clearTimeout(hoverDebounceTimer)
        hoverDebounceTimer = null
      }
      if (autoRemoveTimer !== null) {
        clearTimeout(autoRemoveTimer)
        autoRemoveTimer = null
      }

      // If it's the same view, just refresh the auto-remove timer
      if (currentHoveredViewId === detail.viewId) {
        // Reset auto-remove timer
        autoRemoveTimer = window.setTimeout(() => {
          const activeElements = document.querySelectorAll('.inline-view-active')
          Array.from(activeElements).forEach(el => {
            el.classList.remove('inline-view-active')
          })
          currentHoveredViewId = null
        }, 3000)
        return
      }

      // Store the new hovered view ID
      currentHoveredViewId = detail.viewId

      // Debounce the hover action (50ms delay to prevent flickering)
      hoverDebounceTimer = window.setTimeout(() => {

      // Always get from inlineViewStore (most reliable)
      const views = get(inlineViewStore).views
      const activeViews = views.filter(v =>
        v.status !== 'applied' && v.status !== 'cancelled' && v.type === 'card'
      )

      // Calculate YAML offset
      let yamlOffset = 0
      if (originalContentWithYaml && originalContentWithYaml.includes('---\n')) {
        const yamlMatch = originalContentWithYaml.match(/^---\n[\s\S]*?\n---\n/)
        if (yamlMatch) {
          yamlOffset = yamlMatch[0].length
        }
      }

      const ranges = activeViews.map(view => ({
        from: Math.max(0, view.position.start - yamlOffset),
        to: Math.max(0, view.position.end - yamlOffset),
        id: view.id
      }))

      // Find the range with matching viewId
      const range = ranges.find((r: any) => r.id === detail.viewId)

      if (range) {
          // Use the native ProseMirror positions from the extension
          const { from, to } = range

          // Validate the positions
          if (from >= 0 && to <= editor.state.doc.content.size && from < to) {
            // First, set selection to the target range
            editor.chain()
              .focus()
              .setTextSelection({ from, to })
              .run()

            // Then scroll to center the selection in view
            requestAnimationFrame(() => {
              // Get the DOM position of the selection
              try {
                const coords = editor.view.coordsAtPos(from)
                const editorWrapper = editor.view.dom.closest('.tiptap-editor-wrapper') as HTMLElement

                if (editorWrapper && coords) {
                  const wrapperRect = editorWrapper.getBoundingClientRect()
                  const scrollTop = editorWrapper.scrollTop

                  // Calculate position relative to the wrapper
                  const relativeTop = coords.top - wrapperRect.top + scrollTop

                  // Calculate target scroll to center the selection
                  const targetScroll = relativeTop - (editorWrapper.clientHeight / 2) + 20 // 20px offset for better visibility

                  editorWrapper.scrollTo({
                    top: Math.max(0, targetScroll),
                    behavior: 'smooth'
                  })
                }
              } catch (e) {
                // Fallback to default scrollIntoView
                editor.commands.scrollIntoView()
              }

            // Highlight the decoration elements immediately (no delay)
            // Remove any existing active highlights first
            const activeElements = document.querySelectorAll('.inline-view-active')
            Array.from(activeElements).forEach(el => {
              el.classList.remove('inline-view-active')
            })

            // Find decorations by view ID
            const decorations = document.querySelectorAll(
              `.inline-view-highlight[data-inline-view-id="${detail.viewId}"]`
            )

            if (decorations && decorations.length > 0) {
              Array.from(decorations).forEach(el => {
                el.classList.add('inline-view-active')
              })

                // Ensure the first decoration is visible in the viewport
                const firstDecoration = decorations[0] as HTMLElement
                const editorWrapper = editor.view.dom.closest('.tiptap-editor-wrapper') as HTMLElement

                if (editorWrapper && firstDecoration) {
                  const wrapperRect = editorWrapper.getBoundingClientRect()
                  const decorationRect = firstDecoration.getBoundingClientRect()

                  // Check if decoration is outside the visible area
                  if (decorationRect.top < wrapperRect.top ||
                      decorationRect.bottom > wrapperRect.bottom) {
                    // Calculate the scroll position to center the decoration
                    const decorationOffsetTop = firstDecoration.offsetTop
                    const wrapperHeight = editorWrapper.clientHeight
                    const targetScroll = decorationOffsetTop - (wrapperHeight / 2) + (decorationRect.height / 2)

                    editorWrapper.scrollTo({
                      top: Math.max(0, targetScroll),
                      behavior: 'smooth'
                    })
                  }
                }
              } else {
                // If no decoration elements found by ID, find by position
                // Get all decorations and check their positions
                const allDecorations = document.querySelectorAll('.inline-view-highlight')

                // Try to find decoration at the specific position
                try {
                  const domAtPos = editor.view.domAtPos(from)
                  if (domAtPos && domAtPos.node) {
                    const node = domAtPos.node.nodeType === Node.TEXT_NODE
                      ? domAtPos.node.parentElement
                      : domAtPos.node as HTMLElement

                    if (node) {
                      // Find the inline-view-highlight parent or itself
                      let highlight = null
                      if (node.classList?.contains('inline-view-highlight')) {
                        highlight = node
                      } else {
                        highlight = node.closest('.inline-view-highlight')
                      }

                      if (highlight) {
                        highlight.classList.add('inline-view-active')
                      } else {
                        // Try to find any decoration that contains this node
                        Array.from(allDecorations).forEach(decoration => {
                          if (decoration.contains(node)) {
                            decoration.classList.add('inline-view-active')
                          }
                        })
                      }
                    }
                  }
                } catch (e) {
                  // Could not find DOM element for position
                }
              }

              // Remove highlight after a delay
              setTimeout(() => {
                const activeElements = document.querySelectorAll('.inline-view-active')
                Array.from(activeElements).forEach(el => {
                  el.classList.remove('inline-view-active')
                })
              }, 3000)
            })
          } else {
            // Invalid range positions
          }
        } else {
          // No range found for viewId

          // Fallback: Get position from inlineViewStore directly
          const views = get(inlineViewStore).views
          const view = views.find(v => v.id === detail.viewId)

          if (view && view.position) {
            // Calculate YAML offset
            let yamlOffset = 0
            if (originalContentWithYaml && originalContentWithYaml.includes('---\n')) {
              const yamlMatch = originalContentWithYaml.match(/^---\n[\s\S]*?\n---\n/)
              if (yamlMatch) {
                yamlOffset = yamlMatch[0].length
              }
            }

            // Convert to editor positions
            const editorFrom = Math.max(0, view.position.start - yamlOffset)
            const editorTo = Math.max(0, view.position.end - yamlOffset)

            // Validate and use positions
            if (editorFrom >= 0 && editorTo <= editor.state.doc.content.size && editorFrom < editorTo) {
              // Set selection and scroll
              editor.chain()
                .focus()
                .setTextSelection({ from: editorFrom, to: editorTo })
                .run()

              // Scroll to center
              requestAnimationFrame(() => {
                try {
                  const coords = editor.view.coordsAtPos(editorFrom)
                  const editorWrapper = editor.view.dom.closest('.tiptap-editor-wrapper') as HTMLElement

                  if (editorWrapper && coords) {
                    const wrapperRect = editorWrapper.getBoundingClientRect()
                    const scrollTop = editorWrapper.scrollTop
                    const relativeTop = coords.top - wrapperRect.top + scrollTop
                    const targetScroll = relativeTop - (editorWrapper.clientHeight / 2) + 20

                    editorWrapper.scrollTo({
                      top: Math.max(0, targetScroll),
                      behavior: 'smooth'
                    })
                  }
                } catch (e) {
                  editor.commands.scrollIntoView()
                }

                // Highlight decorations
                const decorations = document.querySelectorAll(
                  `.inline-view-highlight[data-inline-view-id="${detail.viewId}"]`
                )

                if (decorations && decorations.length > 0) {
                  Array.from(decorations).forEach(el => {
                    el.classList.add('inline-view-active')
                  })
                } else {
                  // Try all decorations if only one exists
                  const allDecorations = document.querySelectorAll('.inline-view-highlight')

                  if (allDecorations.length === 1) {
                    allDecorations[0].classList.add('inline-view-active')
                  }
                }

                setTimeout(() => {
                  const activeElements = document.querySelectorAll('.inline-view-active')
                  Array.from(activeElements).forEach(el => {
                    el.classList.remove('inline-view-active')
                  })
                }, 3000)
              })

              return // Exit after handling
            }
          }

          // Final fallback: Try to find and highlight by data attribute
          const decorations = document.querySelectorAll(
            `.inline-view-highlight[data-inline-view-id="${detail.viewId}"]`
          )

          if (decorations && decorations.length > 0) {
            // Convert NodeList to Array for compatibility
            Array.from(decorations).forEach(el => {
              el.classList.add('inline-view-active')
            })

            // Scroll to the first decoration
            const firstDecoration = decorations[0] as HTMLElement
            if (firstDecoration) {
              firstDecoration.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              })
            }

            // Set auto-remove timer
            autoRemoveTimer = window.setTimeout(() => {
              const activeElements = document.querySelectorAll('.inline-view-active')
              Array.from(activeElements).forEach(el => {
                el.classList.remove('inline-view-active')
              })
              currentHoveredViewId = null
            }, 3000)
          }
        }
      }, 50) // 50ms debounce to prevent flickering
    }

    const handleInlineViewHoverEnd = () => {
      if (!editor) return

      // Clear any pending timers
      if (hoverDebounceTimer !== null) {
        clearTimeout(hoverDebounceTimer)
        hoverDebounceTimer = null
      }
      if (autoRemoveTimer !== null) {
        clearTimeout(autoRemoveTimer)
        autoRemoveTimer = null
      }

      // Remove active highlight from decorations immediately
      const activeDecorations = document.querySelectorAll('.inline-view-active')
      Array.from(activeDecorations).forEach(el => {
        el.classList.remove('inline-view-active')
      })

      // Clear the current hovered view ID
      currentHoveredViewId = null

      // Clear text selection to remove focus
      if (editor.state.selection) {
        editor.commands.blur()
      }
    }

    window.addEventListener('inline-view-hover', handleInlineViewHover as EventListener)
    window.addEventListener('inline-view-hover-end', handleInlineViewHoverEnd)

    return () => {
      // Clean up timers
      if (hoverDebounceTimer !== null) {
        clearTimeout(hoverDebounceTimer)
        hoverDebounceTimer = null
      }
      if (autoRemoveTimer !== null) {
        clearTimeout(autoRemoveTimer)
        autoRemoveTimer = null
      }

      window.removeEventListener('inline-view-hover', handleInlineViewHover as EventListener)
      window.removeEventListener('inline-view-hover-end', handleInlineViewHoverEnd)
    }
  })

  // Handle image deletion events
  $effect(() => {
    const unsubscribe = imageDeleteEvents.subscribe((event) => {
      if (!event || !editor) return

      const deletedPath = event.imagePath

      // Find all image wrappers in the DOM and force reload
      const imageWrappers = editor.view.dom.querySelectorAll('.image-wrapper')
      imageWrappers.forEach((wrapper) => {
        const img = wrapper.querySelector('img.tiptap-image') as HTMLImageElement
        if (!img || !img.src) return

        // Normalize paths for comparison
        const imgSrcPath = decodeURIComponent(img.src.replace(/^file:\/\//, '').replace(/\?t=\d+$/, ''))
        const normalizedDeletedPath = deletedPath.replace(/\\/g, '/')

        // Check if paths match
        if (imgSrcPath.includes(normalizedDeletedPath) ||
            normalizedDeletedPath.includes(imgSrcPath.split('/').pop() || '')) {
          // Force reload by clearing and resetting src with cache buster
          const originalSrc = img.src.replace(/\?t=\d+$/, '')
          img.src = ''
          setTimeout(() => {
            img.src = originalSrc + `?t=${Date.now()}`
          }, 0)
        }
      })
    })

    return () => unsubscribe()
  })

  // Cleanup
  onDestroy(() => {
    cancelScheduledContentUpdate()

    // Remove copy event listener
    if (editorElement) {
      editorElement.removeEventListener('copy', handleCopy)
    }

    if (unsubscribeSearch) {
      unsubscribeSearch()
    }
    if (unsubscribeInlineView) {
      unsubscribeInlineView()
    }
    if (linkPreviewCleanup) {
      linkPreviewCleanup()
      linkPreviewCleanup = null
    }
    hideLinkPreview(false)
    if (linkPreviewElement && linkPreviewElement.parentNode) {
      linkPreviewElement.parentNode.removeChild(linkPreviewElement)
    }
    linkPreviewElement = null
    linkPreviewUrlElement = null
    linkPreviewOpenButton = null
    linkPreviewEditButton = null
    linkPreviewRemoveButton = null
    activeLinkElement = null
    if (editor) {
      // Try to clear history before destroying
      try {
        // Get the history plugin and clear it
        const { state, dispatch } = editor.view
        const historyPlugin = state.plugins.find(p => 
          p.spec.key && typeof p.spec.key.key === 'string' && 
          p.spec.key.key.includes('history')
        )
        
        if (historyPlugin && historyPlugin.spec.key) {
          const tr = state.tr
          tr.setMeta(historyPlugin.spec.key, {
            done: { items: [], eventCount: 0 },
            undone: { items: [], eventCount: 0 }
          })
          tr.setMeta('addToHistory', false)
          dispatch(tr)
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      
      editor.destroy()
      editor = null
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('flush-editor-content', handleFlushRequest)
    }
  })
  
  // Public methods
  export function focus(position?: 'start' | 'end' | 'all' | number | boolean | null) {
    if (editor && !editor.isDestroyed) {
      // Use provided position or default to 'end'
      editor.commands.focus(position || 'end')
    }
  }
  
  export function blur() {
    editor?.commands.blur()
  }
  
  export function select() {
    editor?.commands.selectAll()
  }
  
  export function getSelection(): string | null {
    if (!editor) return null
    
    const { from, to } = editor.state.selection
    // Use '\n\n' as block separator to preserve paragraph structure
    const text = editor.state.doc.textBetween(from, to, '\n\n')
    return text || null
  }

  export function getSelectionAsMarkdown(): string | null {
    if (!editor) return null
    
    const { from, to } = editor.state.selection
    
    // Convert to markdown using custom processing for blockquotes
    const storage = editor.storage.markdown
    if (storage && storage.getMarkdown) {
      try {
        const selectedContent = []
        
        // Helper function to convert inline marks to markdown
        const serializeInlineContent = (node) => {
          let result = ''
          node.content.forEach((child) => {
            if (child.isText) {
              let text = child.text
              // Apply marks
              child.marks.forEach(mark => {
                switch (mark.type.name) {
                  case 'bold':
                    text = `**${text}**`
                    break
                  case 'italic':
                    text = `*${text}*`
                    break
                  case 'code':
                    text = `\`${text}\``
                    break
                  case 'strike':
                    text = `~~${text}~~`
                    break
                  case 'link':
                    text = `[${text}](${mark.attrs.href})`
                    break
                }
              })
              result += text
            } else if (child.type.name === 'text') {
              result += child.text
            }
          })
          return result || node.textContent
        }
        
        // Helper to convert a node to markdown
        const nodeToMarkdown = (node, insideBlockquote = false) => {
          const lines = []
          
          if (node.type.name === 'blockquote') {
            // Process blockquote content
            node.content.forEach(child => {
              const childMarkdown = nodeToMarkdown(child, true)
              if (childMarkdown) {
                lines.push(childMarkdown)
              }
            })
            return lines.join('\n')
          } else if (node.type.name === 'paragraph') {
            const content = serializeInlineContent(node)
            if (insideBlockquote) {
              if (content.trim() === '') {
                return '>'
              } else {
                return content.split('\n').map(line => `> ${line}`).join('\n')
              }
            }
            return content
          } else if (node.type.name === 'heading') {
            const level = node.attrs.level || 1
            const prefix = '#'.repeat(level)
            const content = `${prefix} ${serializeInlineContent(node)}`
            if (insideBlockquote) {
              return `> ${content}`
            }
            return content
          } else if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
            const items = []
            node.content.forEach((item, index) => {
              const prefix = node.type.name === 'bulletList' ? '-' : `${index + 1}.`
              let itemContent = ''
              if (item.content.size > 0) {
                item.content.forEach(child => {
                  if (child.type.name === 'paragraph') {
                    itemContent += serializeInlineContent(child)
                  } else if (child.type.name === 'bulletList' || child.type.name === 'orderedList') {
                    // Nested list
                    const nestedList = nodeToMarkdown(child, insideBlockquote)
                    if (nestedList) {
                      itemContent += '\n' + nestedList.split('\n').map(line => '  ' + line).join('\n')
                    }
                  } else {
                    itemContent += child.textContent
                  }
                })
              } else {
                itemContent = item.textContent
              }
              
              if (insideBlockquote) {
                items.push(`> ${prefix} ${itemContent}`)
              } else {
                items.push(`${prefix} ${itemContent}`)
              }
            })
            return items.join('\n')
          } else if (node.type.name === 'horizontalRule') {
            return insideBlockquote ? '> ---' : '---'
          } else if (node.type.name === 'codeBlock') {
            const lang = node.attrs.language || ''
            const code = node.textContent || ''
            const codeBlock = `\`\`\`${lang}\n${code}\n\`\`\``
            if (insideBlockquote) {
              return codeBlock.split('\n').map(line => `> ${line}`).join('\n')
            }
            return codeBlock
          }
          
          // Default: return text content
          const text = node.textContent
          if (text && text.trim()) {
            return insideBlockquote ? `> ${text}` : text
          }
          return null
        }
        
        // Process the selection
        const slice = editor.state.doc.slice(from, to)

        // Process each top-level node in the selection
        slice.content.forEach(node => {
          const markdown = nodeToMarkdown(node)
          if (markdown) {
            selectedContent.push(markdown)
          }
        })

        // Join with double newlines to preserve paragraph breaks
        const markdown = selectedContent.filter(c => c).join('\n\n')

        return markdown || null
      } catch (error) {
        console.error('Failed to convert selection to markdown:', error)
        // Fallback to plain text with proper paragraph separation
        return editor.state.doc.textBetween(from, to, '\n\n') || null
      }
    }
    
    // Fallback to plain text with proper paragraph separation
    return editor.state.doc.textBetween(from, to, '\n\n') || null
  }
  
  // Add selection highlight decoration
  export function highlightSelection(from: number, to: number) {
    if (!editor) return
    
    // Add a temporary decoration to highlight the selection
    editor.chain()
      .command(({ tr }) => {
        tr.setMeta('generationSelectionHighlight', { action: 'set', from, to })
        return true
      })
      .run()
  }
  
  // Handle streaming chunk - similar to source mode approach
  function handleStreamingChunk(chunk: string) {
    if (!editor) return
    
    // Get current cursor position if this is the first chunk
    if (streamingStartPos === -1) {
      const { from } = editor.state.selection
      streamingStartPos = from
      streamingBuffer = ''
      streamingUnescapedBuffer = ''
    }
    
    // Unescape the chunk first
    const unescapedChunk = chunk.replace(/\\\*/g, '*')
                               .replace(/\\_/g, '_')
                               .replace(/\\`/g, '`')
                               .replace(/\\~/g, '~')
                               .replace(/\\#/g, '#')
                               .replace(/\\>/g, '>')
                               .replace(/\\-/g, '-')
    
    // Calculate previous length (same as source mode)
    const previousLength = streamingUnescapedBuffer.length
    
    // Add to both buffers
    streamingBuffer += chunk
    streamingUnescapedBuffer += unescapedChunk
    
    // Replace text with accumulated stream (like source mode)
    // Use transaction to replace previous text with new accumulated text
    editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        if (dispatch) {
          // Delete previous streamed content
          if (previousLength > 0) {
            tr.delete(streamingStartPos, streamingStartPos + previousLength)
          }
          // Insert accumulated unescaped text as plain text
          tr.insertText(streamingUnescapedBuffer, streamingStartPos)
        }
        return true
      })
      .run()
    
    // Auto-scroll to keep generated text visible during streaming
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (editorElement) {
          // Get the wrapper element which handles scrolling
          const wrapper = editorElement.parentElement
          if (!wrapper) return
          
          // Get the position where text is being inserted (end of streamed text)
          const streamEndPos = streamingStartPos + streamingUnescapedBuffer.length
          
          // Try to get the DOM position of the streaming position
          try {
            // Get the position in the document
            const coords = editor.view.coordsAtPos(streamEndPos)
            const editorRect = wrapper.getBoundingClientRect()
            
            // Calculate relative position
            const relativeTop = coords.top - editorRect.top
            const visibleHeight = wrapper.clientHeight
            
            // If the cursor is below the visible area, scroll down
            if (relativeTop > visibleHeight - 100) {
              // Scroll to keep cursor in the middle-lower part of the view
              const targetScroll = wrapper.scrollTop + (relativeTop - visibleHeight * 0.6)
              wrapper.scrollTop = targetScroll
            }
            
            // If cursor is above the visible area, scroll up
            if (relativeTop < 100) {
              const targetScroll = wrapper.scrollTop + (relativeTop - visibleHeight * 0.3)
              wrapper.scrollTop = Math.max(0, targetScroll)
            }
          } catch (error) {
            // Fallback: use scrollIntoView command
            editor.commands.scrollIntoView()
          }
        }
      })
    })
  }
  
  // Process the complete streamed text with markdown when streaming finishes
  export function finalizeStreamingContent() {
    if (!editor || !markdownRenderer || streamingUnescapedBuffer.length === 0 || streamingStartPos === -1) return

    // Check if the text actually contains markdown formatting
    // If it's just plain text, skip formatting to avoid creating unnecessary paragraph breaks
    const hasMarkdown = /[*_#\[\]`~>-]|^\d+\.|^-\s|^\*\s/m.test(streamingUnescapedBuffer)

    if (!hasMarkdown) {
      // Just reset state without reformatting - text was already inserted correctly during streaming
      resetStreamingState()
      return
    }

    // Replace the plain text with formatted markdown content
    editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        if (dispatch) {
          try {
            // Delete the plain text that was inserted during streaming
            const textLength = streamingUnescapedBuffer.length
            tr.delete(streamingStartPos, streamingStartPos + textLength)

            // Insert the entire unescaped buffer with markdown formatting
            markdownRenderer.renderMarkdown(tr, streamingStartPos, streamingUnescapedBuffer)
          } catch (error) {
            console.error('Error finalizing streaming content:', error)
            // Try to insert at original position as fallback
            markdownRenderer.renderMarkdown(tr, streamingStartPos, streamingUnescapedBuffer)
          }
        }
        return true
      })
      .run()

    // Reset streaming state
    resetStreamingState()
  }
  
  // Reset streaming state when generation completes
  export function resetStreamingState() {
    streamingBuffer = ''
    streamingUnescapedBuffer = ''
    streamingStartPos = -1
    streamingPlaceholderNode = null
  }
  
  // Clear selection highlight decoration  
  export function clearSelectionHighlight() {
    if (!editor) return
    
    editor.chain()
      .command(({ tr }) => {
        tr.setMeta('generationSelectionHighlight', { action: 'clear' })
        return true
      })
      .run()
  }
  
  // Expose editor instance for selection handling
  export { editor }
  
  // Streaming buffer to handle incomplete markdown patterns
  let streamingBuffer = ''
  let streamingUnescapedBuffer = '' // Track actual inserted text
  let streamingStartPos = -1
  let streamingPlaceholderNode: any = null
  
  export function replaceText(start: number, end: number, newText: string): void {
    if (!editor || !markdownRenderer) return

    // Special handling for streaming mode (indicated by start === -1)
    if (start === -1) {
      // For streaming, we need to handle markdown patterns that might span chunks
      handleStreamingChunk(newText)
      return
    }

    // The positions passed from InlineViewHighlight are already ProseMirror positions
    // Use transaction to atomically replace and format text
    editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        if (dispatch) {
          // Check if the selection is within a single block (e.g., paragraph)
          const startPos = tr.doc.resolve(start)
          const endPos = tr.doc.resolve(end)
          const sameParent = startPos.parent === endPos.parent
          const isParagraph = startPos.parent.type.name === 'paragraph'

          // If we're replacing text within a single paragraph, do inline replacement
          if (sameParent && isParagraph) {
            // Delete the old content
            tr.delete(start, end)

            // Parse the new text for inline markdown formatting
            const inlineNodes = markdownRenderer.parseAndCreateInlineNodes ?
              markdownRenderer.parseAndCreateInlineNodes(newText) :
              [editor.schema.text(newText)]

            // Insert the inline content at the position
            let insertPos = start
            for (const node of inlineNodes) {
              tr.insert(insertPos, node)
              insertPos += node.nodeSize
            }
          } else {
            // For cross-block replacements, use the original logic
            tr.delete(start, end)
            markdownRenderer.renderMarkdown(tr, start, newText)
          }
        }
        return true
      })
      .run()

    // Force markdown storage update after text replacement
    // This ensures the markdown representation is in sync
    if (editor.storage.markdown && editor.storage.markdown.getMarkdown) {
      editor.storage.markdown.getMarkdown()
    }

    // Ensure the cursor position is visible
    requestAnimationFrame(() => {
      editor.commands.scrollIntoView()
    })
  }
  
  export function getTextContent(): string {
    if (!editor) return ''
    // Return the markdown content without YAML
    // Try to get markdown from the storage
    const storage = editor.storage.markdown
    if (storage && storage.getMarkdown) {
      try {
        let markdown = storage.getMarkdown()
        if (markdown) {
          // Fix escaped special characters
          // Remove unnecessary escaping of asterisks, underscores, etc.
          markdown = markdown.replace(/\\(\*|_|`|~|#|>|-)/g, '$1')

          // Fix escaped image syntax: !\[\](...) -> ![](...)
          markdown = markdown.replace(/!\\\[(.*?)\\\]\(([^)]+)\)/g, '![$1]($2)')

          // Fix escaped link syntax: \[text\](url) -> [text](url)
          // But not for images (handled above) or footnotes (handled below)
          markdown = markdown.replace(/(?<!!)\\\[((?!\^).*?)\\\]\(([^)]+)\)/g, '[$1]($2)')

          // Fix escaped footnote brackets
          // Convert \[^n\]: back to [^n]: for footnote references
          markdown = markdown.replace(/\\\[(\^[\d]+)\\\]:/g, '[$1]:')
          // Also fix inline footnote references \[^n\] to [^n]
          markdown = markdown.replace(/\\\[(\^[\d]+)\\\]/g, '[$1]')

          return markdown
        }
      } catch (error) {
        console.error('[TipTap] Error getting markdown:', error)
      }
    }
    // Fallback to text content if markdown storage not found
    return editor.state.doc.textContent
  }
  
  export function getEditorElement(): HTMLElement | null {
    return editorElement
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="tiptap-editor-wrapper {className}"
  onclick={handleEditorClick}
>
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div
    bind:this={editorElement}
    class="tiptap-editor"
  ></div>
  
  <!-- Bubble Menu is mounted programmatically -->
  
  <!-- Character counter -->
  <div 
    class="char-counter"
    style="bottom: {isPromptOpen && promptHeight > 0 ? promptHeight + 16 : 10}px"
  >
    <span class="char-count">
      {characterCount.toLocaleString()}자
    </span>
  </div>
</div>

<style>
  /* TipTap editor wrapper */
  .tiptap-editor-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    background: transparent; /* Match existing editor */
    cursor: text; /* Show text cursor on entire wrapper */
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    scroll-behavior: smooth;
    will-change: scroll-position;
  }
  
  /* TipTap editor container */
  .tiptap-editor {
    width: 100%;
    height: auto;
    min-height: 100%;
    overflow: visible; /* Let parent handle scrolling */
  }
  
  /* ProseMirror container - matching BaseEditor styles */
  :global(.tiptap-editor .ProseMirror) {
    width: 100%;
    min-height: 100%;
    padding: 20px max(32px, calc(50% - 500px));
    font-family: var(--editor-font-family, inherit);
    font-size: var(--editor-font-size, 16px);
    line-height: var(--editor-line-height, 1.8);
    color: var(--text-primary);
    background: transparent;
    outline: none;
    /* Match BaseEditor text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeSpeed;
    font-kerning: normal;
    font-feature-settings: normal;
    font-variant-ligatures: none;
  }
  
  /* Focus styles */
  :global(.tiptap-editor .ProseMirror:focus) {
    outline: none;
  }
  
  /* Placeholder - matching BaseEditor */
  :global(.tiptap-editor .ProseMirror.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    color: var(--text-tertiary);
    pointer-events: none;
    height: 0;
    float: left;
  }
  
  /* Typography styles */
  :global(.tiptap-editor h1),
  :global(.tiptap-editor h2),
  :global(.tiptap-editor h3),
  :global(.tiptap-editor h4),
  :global(.tiptap-editor h5),
  :global(.tiptap-editor h6) {
    font-weight: 600;
    margin: 1.5em 0 0.3em;
    line-height: 1.4;
  }

  /* 문서 시작 부분의 첫 제목은 margin-top 제거 */
  :global(.tiptap-editor h1:first-child),
  :global(.tiptap-editor h2:first-child),
  :global(.tiptap-editor h3:first-child),
  :global(.tiptap-editor h4:first-child),
  :global(.tiptap-editor h5:first-child),
  :global(.tiptap-editor h6:first-child) {
    margin-top: 0.5em;
  }
  
  :global(.tiptap-editor h1) {
    font-size: calc(var(--editor-font-size, 16px) * 1.4); /* 기존 1.5 → 1.4 */
  }
  
  :global(.tiptap-editor h2) {
    font-size: calc(var(--editor-font-size, 16px) * 1.25); /* 기존 1.3 → 1.25 */
  }
  
  :global(.tiptap-editor h3) {
    font-size: calc(var(--editor-font-size, 16px) * 1.12); /* 기존 1.15 → 1.12 */
  }
  
  :global(.tiptap-editor h4) {
    font-size: calc(var(--editor-font-size, 16px) * 1.05); /* 유지 */
  }
  
  :global(.tiptap-editor h5) {
    font-size: var(--editor-font-size, 16px); /* 유지 */
  }
  
  :global(.tiptap-editor h6) {
    font-size: calc(var(--editor-font-size, 16px) * 0.92); /* 기존 0.9 → 0.92 */
  }
  
  :global(.tiptap-editor p) {
    margin: 0.75em 0;
    line-height: 1.6;
  }
  
  :global(.tiptap-editor blockquote) {
    margin: 0.5em 0;
    padding-left: 1.5em;
    border-left: 3px solid var(--border-secondary);
    color: var(--text-secondary);
    font-size: calc(var(--editor-font-size, 16px) * 0.95); /* blockquote는 약간 작게 */
  }
  
  /* Blockquote 내부의 heading들은 blockquote 폰트를 기준으로 상대적 크기 적용 (비율 축소) */
  :global(.tiptap-editor blockquote h1) {
    font-size: 1.3em; /* blockquote 폰트 기준 1.3배 (기존 1.5) */
  }
  
  :global(.tiptap-editor blockquote h2) {
    font-size: 1.2em; /* blockquote 폰트 기준 1.2배 (기존 1.3) */
  }
  
  :global(.tiptap-editor blockquote h3) {
    font-size: 1.1em; /* blockquote 폰트 기준 1.1배 (기존 1.15) */
  }
  
  :global(.tiptap-editor blockquote h4) {
    font-size: 1.05em; /* blockquote 폰트 기준 1.05배 */
  }
  
  :global(.tiptap-editor blockquote h5) {
    font-size: 1em; /* blockquote 폰트와 동일 */
  }
  
  :global(.tiptap-editor blockquote h6) {
    font-size: 0.95em; /* blockquote 폰트 기준 0.95배 (기존 0.9) */
  }
  
  /* 중첩된 blockquote의 경우도 고려 */
  :global(.tiptap-editor blockquote blockquote) {
    font-size: 1em; /* 부모 blockquote 크기 유지 */
  }
  
  /* Lists - compact style */
  :global(.tiptap-editor ul),
  :global(.tiptap-editor ol) {
    margin: 0.5em 0 !important;
    padding-left: 1.5em;
  }
  
  :global(.tiptap-editor ul) {
    list-style-type: disc;
    list-style-position: outside;
  }
  
  /* 리스트 내부의 heading들도 리스트 폰트를 기준으로 (비율 축소) */
  :global(.tiptap-editor li h1) {
    font-size: 1.25em; /* 리스트 내부에서는 더 작게 (기존 1.4) */
  }
  
  :global(.tiptap-editor li h2) {
    font-size: 1.15em; /* 기존 1.25 */
  }
  
  :global(.tiptap-editor li h3) {
    font-size: 1.08em; /* 기존 1.1 */
  }
  
  :global(.tiptap-editor li h4) {
    font-size: 1.02em;
  }
  
  :global(.tiptap-editor li h5) {
    font-size: 0.98em;
  }
  
  :global(.tiptap-editor li h6) {
    font-size: 0.92em;
  }
  
  /* Blockquote 안의 리스트 heading은 더욱 축소 */
  :global(.tiptap-editor blockquote li h1) {
    font-size: 1.15em;
  }
  
  :global(.tiptap-editor blockquote li h2) {
    font-size: 1.08em;
  }
  
  :global(.tiptap-editor blockquote li h3) {
    font-size: 1.02em;
  }
  
  :global(.tiptap-editor ol) {
    list-style-type: decimal;
    list-style-position: outside;
  }
  
  :global(.tiptap-editor li) {
    margin: 0.2em 0 !important;
    padding: 0 !important;
    line-height: 1.6 !important;
    display: list-item;
  }
  
  :global(.tiptap-editor li + li) {
    margin-top: 0.2em !important;
  }
  
  :global(.tiptap-editor li p) {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Code - inline code */
  :global(.tiptap-editor code) {
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    font-size: 0.9em;
    padding: 0.2em 0.4em;
    background: var(--bg-secondary);
    border-radius: 3px;
    color: var(--text-primary);
  }
  
  :global(.tiptap-editor pre) {
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    font-size: 0.9em;
    padding: 1em;
    background: var(--bg-secondary);
    border-radius: 4px;
    overflow-x: auto;
    margin: 1em 0;
  }
  
  :global(.tiptap-editor pre code) {
    padding: 0;
    background: transparent;
  }
  
  /* Links */
  :global(.tiptap-editor a[href]),
  :global(.tiptap-editor .tiptap-link[href]) {
    color: var(--accent-primary);
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    text-decoration-color: color-mix(in srgb, var(--accent-primary) 55%, transparent 45%);
    text-underline-offset: 3px;
    cursor: text;
    transition: color 160ms ease, background-color 160ms ease, text-decoration-color 160ms ease;
    border-radius: 2px;
    padding: 0 1px;
  }
  
  :global(.tiptap-editor a[href].is-hovered-link),
  :global(.tiptap-editor .tiptap-link[href].is-hovered-link),
  :global(.tiptap-editor a[href]:hover),
  :global(.tiptap-editor .tiptap-link[href]:hover) {
    color: color-mix(in srgb, var(--accent-primary) 85%, var(--text-primary) 15%);
    background-color: color-mix(in srgb, var(--accent-primary) 18%, transparent 82%);
    text-decoration-color: color-mix(in srgb, var(--accent-primary) 90%, transparent 10%);
  }

  :global(.tiptap-link-preview) {
    position: fixed;
    z-index: 9999;
    min-width: 220px;
    max-width: min(360px, calc(100vw - 32px));
    padding: 10px 12px;
    border-radius: 10px;
    background: color-mix(in srgb, var(--bg-secondary) 88%, var(--bg-primary) 12%);
    color: var(--text-primary);
    border: 1px solid color-mix(in srgb, var(--border-secondary) 65%, var(--border-primary) 35%);
    box-shadow: 0 18px 38px rgba(15, 15, 15, 0.2);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(-6px);
    transition: opacity 140ms ease, transform 140ms ease;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 12px;
    line-height: 1.5;
  }

  :global(.tiptap-link-preview.is-visible) {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }

  :global(.tiptap-link-preview-url) {
    font-family: 'JetBrains Mono', 'Menlo', 'Fira Code', 'SFMono-Regular', monospace;
    font-size: 12px;
    color: color-mix(in srgb, var(--text-primary) 88%, var(--accent-primary) 12%);
    word-break: break-word;
    text-decoration: none;
  }

  :global(.tiptap-link-preview-actions) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  :global(.tiptap-link-preview-buttons) {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  :global(.tiptap-link-preview-button) {
    font-size: 11px;
    line-height: 1;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease, transform 140ms ease;
  }

  :global(.tiptap-link-preview-button.primary) {
    background: color-mix(in srgb, var(--accent-primary) 92%, var(--accent-secondary) 8%);
    color: #ffffff;
    border-color: transparent;
  }

  :global(.tiptap-link-preview-button.primary:hover) {
    background: color-mix(in srgb, var(--accent-secondary) 80%, var(--accent-primary) 20%);
    transform: translateY(-1px);
  }

  :global(.tiptap-link-preview-button.primary:active) {
    transform: translateY(0);
  }

  :global(.tiptap-link-preview-button.secondary) {
    background: transparent;
    color: color-mix(in srgb, var(--text-secondary) 80%, var(--text-primary) 20%);
    border-color: color-mix(in srgb, var(--border-secondary) 70%, var(--border-primary) 30%);
  }

  :global(.tiptap-link-preview-button.secondary:hover) {
    color: var(--text-primary);
    border-color: color-mix(in srgb, var(--border-secondary) 90%, var(--border-primary) 10%);
    background: color-mix(in srgb, var(--accent-primary) 10%, transparent 90%);
  }

  :global(.tiptap-link-preview-button.danger) {
    background: transparent;
    color: color-mix(in srgb, var(--error) 75%, var(--text-secondary) 25%);
    border-color: color-mix(in srgb, var(--error) 30%, transparent 70%);
  }

  :global(.tiptap-link-preview-button.danger:hover) {
    color: var(--error);
    border-color: color-mix(in srgb, var(--error) 55%, transparent 45%);
    background: color-mix(in srgb, var(--error) 12%, transparent 88%);
  }

  :global(.tiptap-link-preview-hint) {
    font-size: 11px;
    color: var(--text-secondary);
    white-space: nowrap;
    align-self: flex-end;
  }
  
  /* Search highlights for ProseMirror decorations */
  :global(.tiptap-editor .search-highlight) {
    background: rgba(255, 235, 59, 0.3);
    border-radius: 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  
  :global(.tiptap-editor .search-current) {
    background: rgba(255, 111, 0, 0.8) !important;
    border-radius: 3px;
    outline: 2px solid rgba(255, 111, 0, 0.5);
    outline-offset: 1px;
  }
  
  /* Dark mode search highlights */
  :global(.dark-theme .tiptap-editor .search-highlight) {
    background: rgba(255, 235, 59, 0.25);
  }
  
  :global(.dark-theme .tiptap-editor .search-current) {
    background: rgba(255, 140, 0, 0.7) !important;
    outline: 2px solid rgba(255, 200, 0, 0.9);
    outline-offset: 1px;
    box-shadow: 0 0 8px rgba(255, 140, 0, 0.4);
  }
  
  /* Inline view highlights for ProseMirror decorations */

  /* Generation selection highlight - for Cmd+K prompt */
  :global(.tiptap-editor .generation-selection-highlight) {
    background: rgba(255, 235, 59, 0.3);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  /* Dark mode generation selection highlight */
  :global(.dark-theme .tiptap-editor .generation-selection-highlight) {
    background: rgba(99, 102, 241, 0.3);
  }

  /* Horizontal rule */
  :global(.tiptap-editor hr) {
    border: none;
    border-top: 1px solid var(--border-primary);
    margin: 2em 0;
  }
  
  /* Text formatting */
  :global(.tiptap-editor strong),
  :global(.tiptap-editor b) {
    font-weight: 700 !important;
    color: var(--text-primary);
  }
  
  :global(.tiptap-editor em),
  :global(.tiptap-editor i) {
    font-style: italic !important;
  }
  
  :global(.tiptap-editor s),
  :global(.tiptap-editor del) {
    text-decoration: line-through;
  }
  
  /* Character counter */
  .char-counter {
    position: fixed;
    bottom: 10px;
    right: 16px;
    padding: 4px 8px;
    font-size: 10px;
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    pointer-events: none;
    user-select: none;
    opacity: 0.6;
    z-index: 10000;
    transition: opacity 0.2s ease, bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: bottom;
    backface-visibility: hidden;
    transform: translateZ(0);
  }
  
  .char-counter:hover {
    opacity: 1;
  }

  .char-count {
    font-weight: 500;
    color: var(--text-secondary);
    transition: color 0.2s ease;
  }

  /* Dark mode support for character counter */
  :global(.dark-theme) .char-count {
    color: var(--text-secondary);
  }

  /* Table styles */
  :global(.tiptap-editor table) {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: auto;
    width: 100%;
    margin: 1.5em 0;
    border-radius: 8px;
    border: 2px solid var(--border-primary);
    overflow: hidden;
  }

  :global(.tiptap-editor table td),
  :global(.tiptap-editor table th) {
    min-width: 1em;
    border-right: 1px solid var(--border-primary);
    border-bottom: 1px solid var(--border-primary);
    padding: 12px 16px;
    vertical-align: middle;
    box-sizing: border-box;
    position: relative;
    text-align: center;
  }

  /* Remove right border from last cell */
  :global(.tiptap-editor table td:last-child),
  :global(.tiptap-editor table th:last-child) {
    border-right: none;
  }

  /* Remove bottom border from last row */
  :global(.tiptap-editor table tr:last-child td) {
    border-bottom: none;
  }

  :global(.tiptap-editor table th) {
    font-weight: 600;
    background-color: var(--bg-secondary);
    color: var(--text-primary);
  }

  /* Header bottom border - only for header row */
  :global(.tiptap-editor table thead tr:last-child th) {
    border-bottom: 2px solid var(--border-primary);
  }

  /* Hover effect for table rows */
  :global(.tiptap-editor table tbody tr:hover) {
    background-color: var(--bg-tertiary);
  }

  /* Striped rows */
  :global(.tiptap-editor table tbody tr:nth-child(even)) {
    background-color: rgba(0, 0, 0, 0.02);
  }

  :global(.dark-theme .tiptap-editor table tbody tr:nth-child(even)) {
    background-color: rgba(255, 255, 255, 0.02);
  }

  /* Column resize handle */
  :global(.tiptap-editor table .column-resize-handle) {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--primary);
    pointer-events: none;
  }

  /* Selected cell */
  :global(.tiptap-editor table .selectedCell:after) {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(59, 130, 246, 0.1);
    pointer-events: none;
  }

  /* Dark mode table styles */
  :global(.dark-theme .tiptap-editor table) {
    border-color: var(--border-primary);
  }

  :global(.dark-theme .tiptap-editor table td),
  :global(.dark-theme .tiptap-editor table th) {
    border-color: var(--border-primary);
  }

  :global(.dark-theme .tiptap-editor table th) {
    background-color: var(--bg-secondary);
    border-bottom-color: var(--border-primary);
  }

  :global(.dark-theme .tiptap-editor table tbody tr:hover) {
    background-color: var(--bg-tertiary);
  }

  /* Responsive table wrapper */
  :global(.tiptap-editor .tableWrapper) {
    overflow-x: auto;
    margin: 1.5em 0;
  }

  /* Table cell alignment */
  :global(.tiptap-editor table td[style*="text-align: center"]),
  :global(.tiptap-editor table th[style*="text-align: center"]) {
    text-align: center;
  }

  :global(.tiptap-editor table td[style*="text-align: right"]),
  :global(.tiptap-editor table th[style*="text-align: right"]) {
    text-align: right;
  }

  /* Image centering */
  :global(.tiptap-editor .image-wrapper) {
    display: block;
    text-align: center;
    margin: 1em auto;
    position: relative;
    width: fit-content;
    max-width: 100%;
  }

  :global(.tiptap-editor .image-wrapper:hover .image-size-menu .menu-button) {
    opacity: 1;
  }

  :global(.tiptap-editor .image-wrapper img) {
    max-width: 100%;
    height: auto;
    display: inline-block;
    vertical-align: middle;
  }

  /* Inline view highlights - default red/error color */
  :global(.tiptap-editor .inline-view-highlight) {
    background-color: rgba(239, 68, 68, 0.15);  /* Red error color by default */
    border-radius: 3px;
    cursor: pointer;
    transition: background-color 0.2s ease, text-decoration-color 0.2s ease;
    /* Wavy underline for error indication */
    text-decoration: underline;
    text-decoration-style: wavy;
    text-decoration-color: rgba(239, 68, 68, 0.6);
    text-decoration-thickness: 1.5px;
    text-underline-offset: 3px;
  }

  :global(.tiptap-editor .inline-view-highlight:hover) {
    background-color: rgba(88, 86, 214, 0.25);  /* Blue on hover */
    text-decoration-color: rgba(88, 86, 214, 0.6);  /* Blue underline on hover */
  }

  /* Active inline view highlight (when card is hovered) */
  :global(.tiptap-editor .inline-view-active) {
    background-color: rgba(88, 86, 214, 0.35) !important;
    text-decoration-color: rgba(88, 86, 214, 0.8) !important;  /* Keep underline visible */
    animation: pulse-highlight 2s ease;
  }

  @keyframes pulse-highlight {
    0%, 100% {
      background-color: rgba(88, 86, 214, 0.35);
    }
    50% {
      background-color: rgba(88, 86, 214, 0.5);
    }
  }

  /* Card highlight flash animation */
  :global(.highlight-flash) {
    animation: flash-highlight 1s ease;
  }

  @keyframes flash-highlight {
    0% {
      background-color: rgba(88, 86, 214, 0.2);
      transform: scale(1);
    }
    50% {
      background-color: rgba(88, 86, 214, 0.3);
      transform: scale(1.02);
    }
    100% {
      background-color: transparent;
      transform: scale(1);
    }
  }

  /* Card decoration hover state */
  :global(.decoration-hover) {
    background-color: rgba(88, 86, 214, 0.1);
    box-shadow: 0 0 0 2px rgba(88, 86, 214, 0.3);
    transition: all 0.2s ease;
  }

  /* Dark mode inline view highlights */
  :global(.dark-theme .tiptap-editor .inline-view-highlight) {
    background-color: rgba(239, 68, 68, 0.25);  /* Red error color in dark mode */
    text-decoration-color: rgba(239, 68, 68, 0.7);  /* Stronger underline in dark mode */
  }

  :global(.dark-theme .tiptap-editor .inline-view-highlight:hover) {
    background-color: rgba(88, 86, 214, 0.35);  /* Blue on hover in dark mode */
    text-decoration-color: rgba(88, 86, 214, 0.7);  /* Blue underline on hover */
  }

  :global(.dark-theme .tiptap-editor .inline-view-active) {
    background-color: rgba(88, 86, 214, 0.45) !important;
    text-decoration-color: rgba(88, 86, 214, 0.9) !important;  /* Keep underline visible in dark mode */
  }

  :global(.dark-theme .decoration-hover) {
    background-color: rgba(88, 86, 214, 0.2);
    box-shadow: 0 0 0 2px rgba(88, 86, 214, 0.4);
  }
</style>
